// Settings → Integrations admin page.
//
// Lists external connectors for the current organisation, lets admins
// add/edit the ServiceNow and Workday connections, and shows a live
// health pill driven by POST /api/integrations/:id/test.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { SharePointAccessTest } from "@/components/sharepoint-access-test";
import { Link, useParams } from "wouter";

// The four connector cards, the AddIntegrationDialog and the page
// itself all share the same active chatbot id (read from the route).
// Rather than prop-drill it through six components we publish it via a
// tiny context so each card pulls it with `useChatbotId()`.
const ChatbotIdContext = createContext<string>("");
const useChatbotId = () => useContext(ChatbotIdContext);
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InlineFormError } from "@/components/ui/inline-form-error";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plug, RefreshCcw, Trash2, Plus, X, Code2, ScrollText, Table as TableIcon, CheckCircle2, AlertTriangle, LifeBuoy, Headphones, Users, Phone, GraduationCap, CalendarClock, School, Bug } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useBranding } from "@/hooks/use-branding";
import { PageShell, PageStat } from "@/components/page-shell";
// The parameter row shape and the row ↔ JSON-Schema helpers live in
// `./integrations.helpers` so they can be unit-tested without dragging
// in this file's React/JSX dependencies. See task #38.
import {
  type ParamType,
  type ParamPlacement,
  type ParamRow,
  propertyToRow,
  buildParamsFromRows,
  buildParamsFromJsonMode,
  rowsFromParsedSchema,
  previewJson,
} from "./integrations.helpers";

// Mirrors what the server returns from `publicIntegration()` — never
// includes the encrypted credential blob. Status comes from the
// connector's `IntegrationStatus` union; we type it loosely here as a
// string fallback only because legacy rows might predate the union.
type IntegrationStatus = "connected" | "error" | "token_expired" | "unconfigured" | "pending_approval" | "active"
  | "token_acquisition_failed" | "authentication_rejected" | "permission_denied" | "resource_unavailable" | "graph_error";

// A single editable sample applicant for the mock Slate integration. Mirrors
// `SlateTools.SunyApplicant` / the server's `SampleApplicantDto`.
interface SlateSampleApplicant {
  name: string;
  email: string;
  campus: string;
  program: string;
  status: string;
  decision: string;
}

interface IntegrationDTO {
  id: string;
  type: string;
  name: string;
  status: IntegrationStatus | null;
  instanceUrl: string | null;
  lastCheckedAt: string | null;
  lastError: string | null;
  configured: boolean;
  createdAt: string;
  // True when the stored config is in sample/mock mode (no outbound calls).
  mock?: boolean;
  // Slate-only: the admin-customised sample applicants the mock
  // `slate_application_status` tool returns. Echoed back so the edit form can
  // hydrate the editable list. Absent / empty falls back to built-in defaults.
  sampleApplicants?: SlateSampleApplicant[];
  // ServiceNow non-secret fields the server is willing to echo back so
  // the edit form can hydrate them.
  impersonate?: boolean;
  defaultAssignmentGroup?: string;
  // Workday non-secret fields. `tenantName` is required for Workday rows
  // (URL path segment); `defaultPtoTimeOffPlan` is optional.
  tenantName?: string;
  defaultPtoTimeOffPlan?: string;
  // Zendesk non-secret fields the server is willing to echo back so the
  // edit form can hydrate them. `zendeskAuthorised` flips true once
  // OAuth has stored a refresh token.
  zendeskSubdomain?: string;
  // Zendesk OAuth client ID — non-secret (visible in the authorize URL), echoed
  // back so the edit form can show what was entered.
  zendeskOauthClientId?: string;
  zendeskScope?: string;
  zendeskDefaultGroupId?: string;
  zendeskDefaultPriority?: "low" | "normal" | "high" | "urgent";
  zendeskDefaultTags?: string[];
  zendeskAuthorised?: boolean;
  // True when the last authorisation did NOT return a refresh token, so the
  // connection can't auto-renew and will drop at token expiry.
  zendeskRefreshMissing?: boolean;
  // Non-secret metadata about the SAVED secret so users can verify a paste
  // actually landed: length + last 4 chars, and whether Zendesk already
  // rejected this exact value at a token exchange.
  zendeskSecretLength?: number;
  zendeskSecretLast4?: string;
  zendeskSecretRejected?: boolean;
  // Custom REST integration fields (only present when type starts with `custom:`).
  custom?: {
    baseUrl: string;
    authType: "none" | "api_key" | "bearer" | "basic";
    authHeaderName?: string;
    authUsername?: string;
    testPath: string;
    successStatuses?: number[];
    headers?: Array<{ name: string; value?: string; secret: boolean }>;
    endpoints?: EndpointDTO[];
  };
}

// Mirrors `CustomEndpoint` on the server. Endpoint metadata is non-secret
// (it's effectively API documentation), so the public DTO carries the
// full shape including JSON-schema parameters and policy defaults.
interface EndpointDTO {
  name: string;
  displayName: string;
  description: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  parameters: {
    type: "object";
    properties: Record<string, {
      type?: string;
      description?: string;
      enum?: unknown[];
      minimum?: number;
      maximum?: number;
    }>;
    required?: string[];
  };
  paramIn?: Record<string, "path" | "query" | "body" | "header">;
  allowedRoles?: string[];
  requiresApproval?: boolean;
  perUserPerHour?: number | null;
  perOrgPerHour?: number | null;
  successStatuses?: number[];
}

interface TestResult {
  ok: boolean;
  kind: IntegrationStatus;
  error?: string;
}

interface SaveIntegrationResponse extends IntegrationDTO {
  test?: TestResult;
  toolSyncWarning?: string | null;
}

function StatusPill({ status }: { status: IntegrationDTO["status"] }) {
  const diagnosticLabels: Partial<Record<IntegrationStatus, string>> = {
    token_acquisition_failed: "Token acquisition failed", authentication_rejected: "Authentication rejected",
    permission_denied: "Graph permission denied", resource_unavailable: "Resource unavailable", graph_error: "Graph request failed",
  };
  if (status && diagnosticLabels[status]) return <Badge variant="destructive">{diagnosticLabels[status]}</Badge>;
  if (status === "connected" || status === "active") {
    return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200" data-testid="badge-status-connected">Connected</Badge>;
  }
  if (status === "pending_approval") {
    return <Badge className="bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/40 dark:text-amber-200" data-testid="badge-status-pending-approval">⏳ Pending Approval</Badge>;
  }
  if (status === "error") {
    return <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200" data-testid="badge-status-error">Error</Badge>;
  }
  if (status === "token_expired") {
    return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200" data-testid="badge-status-token-expired">Token expired</Badge>;
  }
  return <Badge variant="secondary" data-testid="badge-status-unconfigured">Not configured</Badge>;
}

// Centralised so save and test paths render the same wording. The
// `connectorLabel` lets the toast distinguish "ServiceNow connected" vs
// "Workday connected" without each card duplicating the strings.
function describeTestResult(test: TestResult | undefined, connectorLabel: string): { title: string; description?: string; ok: boolean } {
  if (!test) return { title: "Saved", ok: true };
  if (test.ok) return { title: `${connectorLabel} connected`, description: "Credentials saved and verified.", ok: true };
  if (test.kind === "token_expired") {
    return { title: "Token expired", description: test.error ?? `${connectorLabel} rejected the OAuth token.`, ok: false };
  }
  const labels: Partial<Record<IntegrationStatus, string>> = {
    token_acquisition_failed: "Token acquisition failed", authentication_rejected: "Authentication rejected",
    permission_denied: "Graph permission denied", resource_unavailable: "Resource unavailable", graph_error: "Graph request failed",
  };
  if (labels[test.kind]) return { title: labels[test.kind]!, description: test.error, ok: false };
  return { title: "Connection failed", description: test.error ?? "Unknown error", ok: false };
}

async function readSaveResponse(res: unknown): Promise<SaveIntegrationResponse | null> {
  if (!(res instanceof Response)) return null;
  try {
    return (await res.json()) as SaveIntegrationResponse;
  } catch {
    return null;
  }
}

// ──────────────────────────────────────────────────────────────────────
// Zendesk card
// ──────────────────────────────────────────────────────────────────────
// Zendesk uses a platform-shared OAuth app: every tenant authenticates
// against ZENDESK_CLIENT_ID/SECRET held as platform secrets, but each
// tenant brings its own subdomain + access/refresh token pair (stored
// per-org, encrypted). The "Connect with Zendesk" button kicks off the
// authorization_code flow at /api/integrations/zendesk/oauth/start.

interface OauthStartResponse { authorizeUrl: string; redirectUri: string; state: string }

function ZendeskCard({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const brandShort = useBranding().shortName;
  const [subdomain, setSubdomain] = useState(existing?.zendeskSubdomain ?? "");
  const [oauthClientId, setOauthClientId] = useState(existing?.zendeskOauthClientId ?? "");
  const [oauthClientSecret, setOauthClientSecret] = useState("");
  const [defaultGroupId, setDefaultGroupId] = useState(existing?.zendeskDefaultGroupId ?? "");
  const [defaultPriority, setDefaultPriority] = useState<"low" | "normal" | "high" | "urgent" | "">(
    existing?.zendeskDefaultPriority ?? "",
  );
  const [defaultTags, setDefaultTags] = useState((existing?.zendeskDefaultTags ?? []).join(", "));

  // Re-hydrate when the row arrives from the server (initial fetch may
  // resolve after the component has mounted with empty fields).
  useEffect(() => {
    if (!existing) return;
    setSubdomain((prev) => prev || existing.zendeskSubdomain || "");
    setOauthClientId((prev) => prev || existing.zendeskOauthClientId || "");
    setDefaultGroupId((prev) => prev || existing.zendeskDefaultGroupId || "");
    if (existing.zendeskDefaultPriority) setDefaultPriority((prev) => prev || existing.zendeskDefaultPriority || "");
    if (existing.zendeskDefaultTags?.length) {
      setDefaultTags((prev) => prev || existing.zendeskDefaultTags!.join(", "));
    }
  }, [existing?.id]);

  const saveDefaultsMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "zendesk",
      name: existing?.name ?? "Zendesk",
      config: {
        subdomain: subdomain.trim().toLowerCase(),
        oauthClientId: oauthClientId.trim() || undefined,
        oauthClientSecret: oauthClientSecret.trim() || undefined,
        defaultGroupId: defaultGroupId.trim() || undefined,
        defaultPriority: defaultPriority || undefined,
        defaultTags: defaultTags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const needsOauth = (json as any)?.needsOauth;
      toast({
        title: "Zendesk defaults saved",
        description: needsOauth
          ? "Click \u201cConnect with Zendesk\u201d to authorise the platform OAuth app for this subdomain."
          : "Default group, priority and tags have been updated.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const oauthStartMut = useMutation({
    // `popup` is a blank tab opened synchronously on click (see button) so we
    // keep the user-gesture and avoid popup blockers. Zendesk's authorize page
    // refuses to render inside an iframe (the Replit preview / canvas), so the
    // flow must run in a top-level browser tab.
    mutationFn: async (popup: Window | null) => {
      // The OAuth start endpoint reads the Client ID/Secret from the saved
      // Integration row, so persist the current form first. This lets the
      // user click "Connect" directly without a separate "Save defaults"
      // step. (Secrets left blank on re-auth keep the existing values.)
      await apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
        type: "zendesk",
        name: existing?.name ?? "Zendesk",
        config: {
          subdomain: subdomain.trim().toLowerCase(),
          oauthClientId: oauthClientId.trim() || undefined,
          oauthClientSecret: oauthClientSecret.trim() || undefined,
          defaultGroupId: defaultGroupId.trim() || undefined,
          defaultPriority: defaultPriority || undefined,
          defaultTags: defaultTags.split(",").map((t) => t.trim()).filter(Boolean),
        },
      });

      // Then hit the start endpoint so the server stashes its CSRF state.
      // Returning JSON (rather than a 302) keeps the API symmetrical and lets
      // us show a toast on error.
      const res = await apiRequest("POST", "/api/integrations/zendesk/oauth/start", {
        subdomain: subdomain.trim().toLowerCase(),
        returnTo: `/chatbots/${chatbotId}/integrations`,
      });
      return { data: (await res.json()) as OauthStartResponse, popup };
    },
    onSuccess: ({ data, popup }) => {
      if (popup && !popup.closed) {
        popup.location.href = data.authorizeUrl;
      } else {
        // Popup was blocked — fall back to navigating the current window.
        // Inside the embedded preview this may be blocked by Zendesk, so try
        // the top-level frame first.
        try { (window.top ?? window).location.href = data.authorizeUrl; }
        catch { window.location.href = data.authorizeUrl; }
      }
    },
    onError: (err: Error, popup) => {
      if (popup && !popup.closed) popup.close();
      toast({ title: "Could not start Zendesk OAuth", description: err.message, variant: "destructive" });
    },
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Zendesk");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "Zendesk responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Zendesk disconnected" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setSubdomain(""); setDefaultGroupId(""); setDefaultPriority(""); setDefaultTags("");
    },
  });

  const subdomainValid = /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/.test(subdomain.trim().toLowerCase());

  // Zendesk rejected the stored secret on the last attempt. Leaving the field
  // blank re-sends that same (revoked) secret, so demand a fresh paste.
  // `zendeskSecretRejected` is the authoritative server-side fingerprint
  // check and wins whenever present (true OR false — false means a new
  // secret was saved after the rejection, so don't block on stale
  // status/lastError text). The regex is only a fallback for servers that
  // don't emit the flag yet.
  const secretRejected = !!existing && (
    typeof existing.zendeskSecretRejected === "boolean"
      ? existing.zendeskSecretRejected
      : (existing.status === "error" && /client secret/i.test(existing.lastError ?? ""))
  );
  const needsFreshSecret = secretRejected && oauthClientSecret.trim().length === 0;
  const typedSecretLen = oauthClientSecret.trim().length;

  return (
    <Card data-testid="card-integration-zendesk">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              Zendesk
              {existing && <StatusPill status={existing.status} />}
            </CardTitle>
            <CardDescription>
              When a chatbot conversation needs a human, {brandShort} will open a Zendesk ticket containing the transcript so a live agent can pick it up.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm" data-testid="button-view-audit-zendesk">
                <Link href="/admin/audit/tools?integration=zendesk">
                  <ScrollText className="w-4 h-4 mr-1" /> View audit log
                </Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={testMut.isPending || !existing.zendeskAuthorised}
                onClick={() => testMut.mutate(existing.id)}
                data-testid="button-test-connection-zendesk"
              >
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-rose-600"
                disabled={deleteMut.isPending}
                onClick={() => deleteMut.mutate(existing.id)}
                data-testid="button-delete-integration-zendesk"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Disconnect
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-zendesk">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-zendesk">
                {existing.lastError}
              </span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="zd-sub">Zendesk subdomain</Label>
            <div className="flex items-center gap-2">
              <Input
                id="zd-sub"
                placeholder="acme"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
                data-testid="input-subdomain-zendesk"
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">.zendesk.com</span>
            </div>
            {!subdomainValid && subdomain.length > 0 && (
              <p className="text-xs text-rose-600" data-testid="text-subdomain-error-zendesk">
                Use the subdomain only (lowercase letters, digits, hyphens; 3\u201363 chars).
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="zd-client-id">OAuth Client ID (unique identifier)</Label>
            <Input
              id="zd-client-id"
              placeholder="e.g. campusassist"
              value={oauthClientId}
              onChange={(e) => setOauthClientId(e.target.value)}
              data-testid="input-oauth-client-id-zendesk"
            />
            {oauthClientId.trim().length > 0 && !/^[a-z0-9][a-z0-9_.\-]{0,63}$/.test(oauthClientId.trim()) && (
              <p className="text-xs text-rose-600" data-testid="text-client-id-error-zendesk">
                This looks like the generated Secret, not the Client ID. Copy the client's{" "}
                <strong>Unique identifier</strong> (short lowercase name) from Zendesk Admin Center
                → Apps and integrations → APIs → OAuth Clients.
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="zd-client-secret">OAuth Client Secret</Label>
            <Input
              id="zd-client-secret"
              type="password"
              placeholder={existing?.zendeskAuthorised ? "Leave blank to keep existing" : "Paste secret from Zendesk"}
              value={oauthClientSecret}
              onChange={(e) => setOauthClientSecret(e.target.value)}
              data-testid="input-oauth-client-secret-zendesk"
            />
            {!!existing?.zendeskSecretLength && typedSecretLen === 0 && (
              <p className="text-xs text-muted-foreground" data-testid="text-secret-saved-zendesk">
                Saved secret: {existing.zendeskSecretLength} characters
                {existing.zendeskSecretLast4 ? <>, ending in <code>…{existing.zendeskSecretLast4}</code></> : null}
                {existing.zendeskSecretRejected && (
                  <span className="text-rose-600"> — Zendesk rejected this value; paste the current one.</span>
                )}
              </p>
            )}
            {typedSecretLen > 0 && typedSecretLen < 64 && (
              <p className="text-xs text-amber-600" data-testid="text-secret-length-warning-zendesk">
                {typedSecretLen} characters pasted — Zendesk Client Secrets are usually 64 characters.
                Make sure you copied the <strong>entire</strong> value (it is shown only once, right
                after creating or regenerating the client).
              </p>
            )}
            {needsFreshSecret && (
              <p className="text-xs text-rose-600" data-testid="text-secret-required-zendesk">
                Zendesk rejected the saved Secret, so it can't be reused. Paste the client's current
                Secret here (if you regenerated it in Zendesk, paste the <strong>new</strong> value)
                before re-authorising.
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="zd-group">Default group ID (optional)</Label>
            <Input
              id="zd-group"
              placeholder="e.g. 360001234567"
              value={defaultGroupId}
              onChange={(e) => setDefaultGroupId(e.target.value)}
              data-testid="input-default-group-zendesk"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="zd-priority">Default priority</Label>
            <Select value={defaultPriority || "normal"} onValueChange={(v) => setDefaultPriority(v as any)}>
              <SelectTrigger id="zd-priority" data-testid="select-default-priority-zendesk">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low" data-testid="option-priority-low">Low</SelectItem>
                <SelectItem value="normal" data-testid="option-priority-normal">Normal</SelectItem>
                <SelectItem value="high" data-testid="option-priority-high">High</SelectItem>
                <SelectItem value="urgent" data-testid="option-priority-urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="zd-tags">Default tags (comma-separated)</Label>
            <Input
              id="zd-tags"
              placeholder="bot, handover"
              value={defaultTags}
              onChange={(e) => setDefaultTags(e.target.value)}
              data-testid="input-default-tags-zendesk"
            />
          </div>
        </div>
        <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="text-zendesk-help">
          <strong>Set-up:</strong> in Zendesk Admin Center \u2192 Apps and integrations \u2192 APIs \u2192 OAuth Clients, create a client
          using <code>{`${typeof window !== "undefined" ? window.location.origin : ""}/api/integrations/zendesk/oauth/callback`}</code>
          {" "}as the redirect URL. The <strong>Client ID</strong> above is the client's{" "}
          <strong>Unique identifier</strong> (the short lowercase name, e.g. <code>campusassist</code>);
          the long generated string is the <strong>Secret</strong> and goes in the Secret field.
          Paste both, click <em>Save defaults</em>, then <em>Connect with Zendesk</em> to authorise.
        </div>
        <InlineFormError error={saveDefaultsMut.isError ? saveDefaultsMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            disabled={saveDefaultsMut.isPending || !subdomainValid}
            onClick={() => saveDefaultsMut.mutate()}
            data-testid="button-save-defaults-zendesk"
          >
            {saveDefaultsMut.isPending ? "Saving\u2026" : "Save defaults"}
          </Button>
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={oauthStartMut.isPending || !subdomainValid}
            onClick={() => {
              if (needsFreshSecret) {
                toast({
                  title: "Paste the new Client Secret first",
                  description:
                    "Zendesk rejected the saved Secret, so re-authorising with it will fail again. Copy the current Secret from Zendesk Admin Center \u2192 Apps and integrations \u2192 APIs \u2192 OAuth Clients (regenerate it if it's hidden), paste it in the Secret field, then click this button.",
                  variant: "destructive",
                });
                document.getElementById("zd-client-secret")?.focus();
                return;
              }
              oauthStartMut.mutate(window.open("about:blank", "_blank"));
            }}
            data-testid="button-connect-zendesk"
          >
            {oauthStartMut.isPending ? "Redirecting\u2026" : (existing?.zendeskAuthorised ? "Re-authorise Zendesk" : "Connect with Zendesk")}
          </Button>
          {existing?.zendeskAuthorised && existing?.zendeskRefreshMissing && (
            <p className="text-xs text-amber-600" data-testid="text-refresh-missing-zendesk">
              Connected, but Zendesk didn't issue a refresh token, so this connection can't auto-renew
              and will disconnect when the access token expires. In Zendesk Admin Center → Apps and
              integrations → APIs → OAuth Clients, enable <strong>token expiration</strong> (rotating
              refresh tokens) for this client, then click <strong>Re-authorise Zendesk</strong>. Your
              existing Client ID and Secret stay the same — no need to generate a new Secret.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// ServiceNow card
// ──────────────────────────────────────────────────────────────────────

function ServiceNowCard({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [instanceUrl, setInstanceUrl] = useState(existing?.instanceUrl ?? "");
  const [name, setName] = useState(existing?.name ?? "ServiceNow");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [impersonate, setImpersonate] = useState(false);
  const [defaultAssignmentGroup, setDefaultAssignmentGroup] = useState("");
  const [mock, setMock] = useState(true);

  // Hydrate non-secret fields when the existing row arrives. Secrets are
  // write-only — the placeholder shows "•••••• (saved)" instead.
  useEffect(() => {
    if (!existing) return;
    setInstanceUrl((prev) => prev || existing.instanceUrl || "");
    setName((prev) => (prev && prev !== "ServiceNow" ? prev : existing.name || "ServiceNow"));
    if (typeof existing.impersonate === "boolean") setImpersonate(existing.impersonate);
    if (typeof existing.mock === "boolean") setMock(existing.mock);
    if (typeof existing.defaultAssignmentGroup === "string") {
      setDefaultAssignmentGroup(existing.defaultAssignmentGroup);
    }
  }, [existing?.id]);

  const saveMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "servicenow",
      name, instanceUrl,
      config: { mock, clientId, clientSecret, username, password, impersonate, defaultAssignmentGroup: defaultAssignmentGroup.trim() || undefined },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "ServiceNow");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setClientSecret(""); setPassword("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "ServiceNow");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "ServiceNow responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "ServiceNow disconnected" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setInstanceUrl(""); setName("ServiceNow");
      setClientId(""); setClientSecret(""); setUsername(""); setPassword(""); setImpersonate(false);
      setDefaultAssignmentGroup(""); setMock(true);
    },
  });

  return (
    <Card data-testid="card-integration-servicenow">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">ServiceNow
              {existing && <StatusPill status={existing.status} />}
              {mock && <Badge variant="secondary" data-testid="badge-mock-servicenow">Mock</Badge>}
            </CardTitle>
            <CardDescription>
              Open and manage IT incidents, search the knowledge base, and look up ticket status.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm" data-testid="button-view-audit-servicenow">
                <Link href="/admin/audit/tools?integration=servicenow">
                  <ScrollText className="w-4 h-4 mr-1" /> View audit log
                </Link>
              </Button>
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-servicenow">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-servicenow">
                <Trash2 className="w-4 h-4 mr-1" /> Disconnect
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-servicenow">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-servicenow">
                {existing.lastError}
              </span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="sn-name">Display name</Label>
            <Input id="sn-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-servicenow" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sn-url">Instance URL</Label>
            <Input id="sn-url" placeholder="https://acme.service-now.com" value={instanceUrl} onChange={(e) => setInstanceUrl(e.target.value)} data-testid="input-instance-url-servicenow" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sn-cid">OAuth client ID</Label>
            <Input id="sn-cid" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="input-client-id-servicenow" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sn-cs">OAuth client secret</Label>
            <Input id="sn-cs" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} data-testid="input-client-secret-servicenow" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sn-user">Service account username</Label>
            <Input id="sn-user" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="input-username-servicenow" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sn-pwd">Service account password</Label>
            <Input id="sn-pwd" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="input-password-servicenow" />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="sn-queue">Default IT request queue (assignment group)</Label>
          <Input
            id="sn-queue"
            placeholder="e.g. IT Service Desk  —  or a sys_id"
            value={defaultAssignmentGroup}
            onChange={(e) => setDefaultAssignmentGroup(e.target.value)}
            data-testid="input-default-assignment-group"
          />
          <p className="text-xs text-muted-foreground">
            All new incidents are routed to this ServiceNow assignment group unless the bot specifies otherwise.
            Accepts a group display name or sys_id. Leave blank to use the instance's default routing rules.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Switch id="sn-imp" checked={impersonate} onCheckedChange={setImpersonate} data-testid="switch-impersonate" />
          <Label htmlFor="sn-imp" className="cursor-pointer">
            Impersonate the requesting employee (audit trail attribution)
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch id="sn-mock" checked={mock} onCheckedChange={setMock} data-testid="switch-mock-servicenow" />
          <Label htmlFor="sn-mock" className="cursor-pointer">
            Use mock mode (no outbound API calls — returns sample incidents/tickets, perfect for demo / setup)
          </Label>
        </div>
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={
              saveMut.isPending ||
              (!mock && (!instanceUrl || (!existing && (!clientId || !clientSecret || !username || !password))))
            }
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-servicenow"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Jira card
// ──────────────────────────────────────────────────────────────────────
// Jira Cloud (yoursite.atlassian.net) authenticates with the user's email
// + an API token (Basic auth) — Atlassian Cloud rejects account passwords.
// The token is write-only; non-secret defaults (site URL, project key,
// issue type) hydrate from the saved row.

function JiraCard({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [name, setName] = useState(existing?.name ?? "Jira");
  const [siteUrl, setSiteUrl] = useState(existing?.instanceUrl ?? "");
  const [email, setEmail] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [defaultProjectKey, setDefaultProjectKey] = useState("");
  const [defaultIssueType, setDefaultIssueType] = useState("");
  const [mock, setMock] = useState(false);

  useEffect(() => {
    if (!existing) return;
    setSiteUrl((prev) => prev || existing.instanceUrl || "");
    setName((prev) => (prev && prev !== "Jira" ? prev : existing.name || "Jira"));
    if (typeof existing.mock === "boolean") setMock(existing.mock);
  }, [existing?.id]);

  const saveMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "jira",
      name,
      instanceUrl: siteUrl,
      config: {
        mock,
        email: email.trim() || undefined,
        apiToken: apiToken.trim() || undefined,
        defaultProjectKey: defaultProjectKey.trim().toUpperCase() || undefined,
        defaultIssueType: defaultIssueType.trim() || undefined,
      },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Jira");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setApiToken("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Jira");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "Jira responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Jira disconnected" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setSiteUrl(""); setName("Jira"); setEmail(""); setApiToken("");
      setDefaultProjectKey(""); setDefaultIssueType(""); setMock(false);
    },
  });

  return (
    <Card data-testid="card-integration-jira">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-[#0052CC]" /> Jira
              {existing && <StatusPill status={existing.status} />}
              {mock && <Badge variant="secondary" data-testid="badge-mock-jira">Mock</Badge>}
            </CardTitle>
            <CardDescription>
              Let the chatbot create Jira issues (requests) and look up the status of an existing request.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm" data-testid="button-view-audit-jira">
                <Link href="/admin/audit/tools?integration=jira">
                  <ScrollText className="w-4 h-4 mr-1" /> View audit log
                </Link>
              </Button>
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-jira">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-jira">
                <Trash2 className="w-4 h-4 mr-1" /> Disconnect
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-jira">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-jira">
                {existing.lastError}
              </span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="jira-name">Display name</Label>
            <Input id="jira-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-jira" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="jira-url">Site URL</Label>
            <Input id="jira-url" placeholder="https://yourcompany.atlassian.net" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} data-testid="input-site-url-jira" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="jira-email">Account email</Label>
            <Input id="jira-email" type="email" placeholder={existing ? "•••••• (saved)" : "you@company.com"} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="input-email-jira" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="jira-token">API token</Label>
            <Input id="jira-token" type="password" placeholder={existing ? "•••••• (saved)" : "Paste API token"} value={apiToken} onChange={(e) => setApiToken(e.target.value)} data-testid="input-api-token-jira" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="jira-project">Default project key</Label>
            <Input id="jira-project" placeholder="e.g. SUP" value={defaultProjectKey} onChange={(e) => setDefaultProjectKey(e.target.value)} data-testid="input-default-project-jira" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="jira-issuetype">Default issue type</Label>
            <Input id="jira-issuetype" placeholder={existing ? "•••••• (saved) — blank keeps it" : "Task"} value={defaultIssueType} onChange={(e) => setDefaultIssueType(e.target.value)} data-testid="input-default-issuetype-jira" />
          </div>
        </div>
        <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="text-jira-help">
          <strong>Set-up:</strong> generate an API token at{" "}
          <code>id.atlassian.com → Security → Create and manage API tokens</code>. Jira Cloud's REST API
          authenticates with your email + API token (not your account password). Issues are created on your behalf.
        </div>
        <div className="flex items-center gap-3">
          <Switch id="jira-mock" checked={mock} onCheckedChange={setMock} data-testid="switch-mock-jira" />
          <Label htmlFor="jira-mock" className="cursor-pointer">
            Use mock mode (no outbound API calls — returns sample issues, useful for demo / setup)
          </Label>
        </div>
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#0052CC] hover:bg-[#0747A6]"
            disabled={saveMut.isPending || (!mock && (!siteUrl || (!existing && (!email || !apiToken))))}
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-jira"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// TeamDynamix card
// ──────────────────────────────────────────────────────────────────────

function TeamDynamixCard({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [instanceUrl, setInstanceUrl] = useState(existing?.instanceUrl ?? "");
  const [name, setName] = useState(existing?.name ?? "TeamDynamix");
  const [beid, setBeid] = useState("");
  const [webServicesKey, setWebServicesKey] = useState("");
  const [appId, setAppId] = useState("");
  const [defaultTypeId, setDefaultTypeId] = useState("");
  const [defaultStatusId, setDefaultStatusId] = useState("");
  const [defaultPriorityId, setDefaultPriorityId] = useState("");
  const [defaultAccountId, setDefaultAccountId] = useState("");
  const [mock, setMock] = useState(true);

  // Hydrate non-secret fields when the existing row arrives. Secrets stay
  // write-only — the placeholder shows "•••••• (saved)" instead.
  useEffect(() => {
    if (!existing) return;
    setInstanceUrl((prev) => prev || existing.instanceUrl || "");
    setName((prev) => (prev && prev !== "TeamDynamix" ? prev : existing.name || "TeamDynamix"));
    if (typeof existing.mock === "boolean") setMock(existing.mock);
  }, [existing?.id]);

  const saveMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "teamdynamix",
      name, instanceUrl,
      config: {
        mock, beid, webServicesKey,
        appId: appId.trim() || undefined,
        defaultTypeId: defaultTypeId.trim() || undefined,
        defaultStatusId: defaultStatusId.trim() || undefined,
        defaultPriorityId: defaultPriorityId.trim() || undefined,
        defaultAccountId: defaultAccountId.trim() || undefined,
      },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "TeamDynamix");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setWebServicesKey("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "TeamDynamix");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "TeamDynamix responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Integration removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setInstanceUrl(""); setName("TeamDynamix");
      setBeid(""); setWebServicesKey(""); setAppId("");
      setDefaultTypeId(""); setDefaultStatusId(""); setDefaultPriorityId(""); setDefaultAccountId("");
      setMock(true);
    },
  });

  return (
    <Card data-testid="card-integration-teamdynamix">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">TeamDynamix
              {existing && <StatusPill status={existing.status} />}
              {mock && <Badge variant="secondary" data-testid="badge-mock-teamdynamix">Mock</Badge>}
            </CardTitle>
            <CardDescription>
              Open and manage IT tickets and look up ticket status in TeamDynamix ITSM.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm" data-testid="button-view-audit-teamdynamix">
                <Link href="/admin/audit/tools?integration=teamdynamix">
                  <ScrollText className="w-4 h-4 mr-1" /> View audit log
                </Link>
              </Button>
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-teamdynamix">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-teamdynamix">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-teamdynamix">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-teamdynamix">
                {existing.lastError}
              </span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="td-name">Display name</Label>
            <Input id="td-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-teamdynamix" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="td-url">Instance URL</Label>
            <Input id="td-url" placeholder="https://acme.teamdynamix.com" value={instanceUrl} onChange={(e) => setInstanceUrl(e.target.value)} data-testid="input-instance-url-teamdynamix" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="td-beid">BEID</Label>
            <Input id="td-beid" value={beid} onChange={(e) => setBeid(e.target.value)} data-testid="input-beid-teamdynamix" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="td-wsk">Web Services Key</Label>
            <Input id="td-wsk" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={webServicesKey} onChange={(e) => setWebServicesKey(e.target.value)} data-testid="input-web-services-key-teamdynamix" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="td-app">Ticketing application ID</Label>
            <Input id="td-app" inputMode="numeric" placeholder="e.g. 31" value={appId} onChange={(e) => setAppId(e.target.value)} data-testid="input-app-id-teamdynamix" />
          </div>
        </div>
        <div className="space-y-1">
          <Label>Default ticket field IDs (optional)</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Input inputMode="numeric" placeholder="Type ID" value={defaultTypeId} onChange={(e) => setDefaultTypeId(e.target.value)} data-testid="input-default-type-id-teamdynamix" />
            <Input inputMode="numeric" placeholder="Status ID" value={defaultStatusId} onChange={(e) => setDefaultStatusId(e.target.value)} data-testid="input-default-status-id-teamdynamix" />
            <Input inputMode="numeric" placeholder="Priority ID" value={defaultPriorityId} onChange={(e) => setDefaultPriorityId(e.target.value)} data-testid="input-default-priority-id-teamdynamix" />
            <Input inputMode="numeric" placeholder="Account ID" value={defaultAccountId} onChange={(e) => setDefaultAccountId(e.target.value)} data-testid="input-default-account-id-teamdynamix" />
          </div>
          <p className="text-xs text-muted-foreground">
            TeamDynamix tickets require numeric field IDs. These defaults are applied to new tickets the bot opens.
            Find them under TDAdmin → your ticketing application. Leave blank if your application allows ticket creation without them.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Switch id="td-mock" checked={mock} onCheckedChange={setMock} data-testid="switch-mock-teamdynamix" />
          <Label htmlFor="td-mock" className="cursor-pointer">
            Use mock mode (no outbound API calls — returns sample tickets, perfect for demo / setup)
          </Label>
        </div>
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={
              saveMut.isPending ||
              (!mock && (!instanceUrl || (!existing && (!beid || !webServicesKey || !appId.trim()))))
            }
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-teamdynamix"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Workday card
// ──────────────────────────────────────────────────────────────────────

function WorkdayCard({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [tenantUrl, setTenantUrl] = useState(existing?.instanceUrl ?? "");
  const [name, setName] = useState(existing?.name ?? "Workday");
  const [tenantName, setTenantName] = useState(existing?.tenantName ?? "");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [defaultPtoTimeOffPlan, setDefaultPtoTimeOffPlan] = useState("");

  // Hydrate non-secret fields when the existing row arrives. Same pattern
  // as the ServiceNow card — secrets stay write-only.
  useEffect(() => {
    if (!existing) return;
    setTenantUrl((prev) => prev || existing.instanceUrl || "");
    setName((prev) => (prev && prev !== "Workday" ? prev : existing.name || "Workday"));
    if (typeof existing.tenantName === "string") setTenantName((p) => p || existing.tenantName!);
    if (typeof existing.defaultPtoTimeOffPlan === "string") {
      setDefaultPtoTimeOffPlan((p) => p || existing.defaultPtoTimeOffPlan!);
    }
  }, [existing?.id]);

  const saveMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "workday",
      name,
      // The integrations API uses a single `instanceUrl` field across
      // connectors; for Workday this carries the tenant host.
      instanceUrl: tenantUrl,
      config: {
        tenantName: tenantName.trim(),
        clientId, clientSecret, refreshToken,
        defaultPtoTimeOffPlan: defaultPtoTimeOffPlan.trim() || undefined,
      },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Workday");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setClientSecret(""); setRefreshToken("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Workday");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "Workday responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Integration removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setTenantUrl(""); setName("Workday"); setTenantName("");
      setClientId(""); setClientSecret(""); setRefreshToken(""); setDefaultPtoTimeOffPlan("");
    },
  });

  return (
    <Card data-testid="card-integration-workday">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">Workday
              {existing && <StatusPill status={existing.status} />}
            </CardTitle>
            <CardDescription>
              PTO balances, time-off requests, payslip access, and the people directory.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm" data-testid="button-view-audit-workday">
                <Link href="/admin/audit/tools?integration=workday">
                  <ScrollText className="w-4 h-4 mr-1" /> View audit log
                </Link>
              </Button>
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-workday">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-workday">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-workday">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-workday">
                {existing.lastError}
              </span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="wd-name">Display name</Label>
            <Input id="wd-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-workday" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="wd-url">Tenant URL</Label>
            <Input id="wd-url" placeholder="https://wd5-impl-services1.workday.com" value={tenantUrl} onChange={(e) => setTenantUrl(e.target.value)} data-testid="input-tenant-url-workday" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="wd-tenant">Tenant name</Label>
            <Input id="wd-tenant" placeholder="acme_dpt1" value={tenantName} onChange={(e) => setTenantName(e.target.value)} data-testid="input-tenant-name-workday" />
            <p className="text-xs text-muted-foreground">The path segment Workday uses in REST URLs (e.g. <code>/ccx/api/v1/<strong>acme_dpt1</strong>/workers</code>).</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="wd-cid">OAuth client ID</Label>
            <Input id="wd-cid" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="input-client-id-workday" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="wd-cs">OAuth client secret</Label>
            <Input id="wd-cs" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} data-testid="input-client-secret-workday" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="wd-rt">Refresh token</Label>
            <Input id="wd-rt" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)} data-testid="input-refresh-token-workday" />
            <p className="text-xs text-muted-foreground">Issued by your Workday API Client for Integrations. We rotate access tokens automatically.</p>
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="wd-pto">Default PTO time-off plan</Label>
          <Input
            id="wd-pto"
            placeholder="e.g. Vacation - USA"
            value={defaultPtoTimeOffPlan}
            onChange={(e) => setDefaultPtoTimeOffPlan(e.target.value)}
            data-testid="input-default-pto-plan"
          />
          <p className="text-xs text-muted-foreground">
            Used by request_pto when the user doesn't pick a plan. Optional — leave blank to require an explicit choice.
          </p>
        </div>
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#0C3547] hover:bg-[#0A2D3A] text-white"
            disabled={
              saveMut.isPending || !tenantUrl || !tenantName ||
              (!existing && (!clientId || !clientSecret || !refreshToken))
            }
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-workday"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Custom integration card — generic REST connector
// ──────────────────────────────────────────────────────────────────────

type AuthType = "none" | "api_key" | "bearer" | "basic";

interface HeaderRow {
  name: string;
  value: string;
  secret: boolean;
  // True when the value field is showing a "•••••• (saved)" placeholder
  // and the admin hasn't typed a replacement yet. We send the empty
  // string for these and the server keeps the stored secret.
  saved?: boolean;
}

// Local editable shape for endpoint rows in the form. Parameters live as
// structured rows; we also remember whether the admin opened the JSON
// view so we can keep showing it across re-renders (and use the JSON as
// the source of truth on save when they edited it directly).
interface EndpointRow {
  name: string;
  displayName: string;
  description: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  params: ParamRow[];
  // True when the admin flipped the per-endpoint "View JSON" toggle.
  // While true, `paramsJson` is the source of truth for save.
  paramsJsonMode: boolean;
  paramsJson: string;
  paramsJsonError: string | null;
  allowedRolesCsv: string;
  requiresApproval: boolean;
  perUserPerHour: string;
}

function endpointDtoToRow(e: EndpointDTO): EndpointRow {
  const required = new Set<string>(e.parameters?.required ?? []);
  const properties = e.parameters?.properties ?? {};
  const params: ParamRow[] = Object.entries(properties).map(([name, pdef]) =>
    propertyToRow(name, pdef, required, e.paramIn as Record<string, ParamPlacement> | undefined),
  );
  return {
    name: e.name,
    displayName: e.displayName,
    description: e.description,
    method: e.method,
    path: e.path,
    params,
    paramsJsonMode: false,
    paramsJson: "",
    paramsJsonError: null,
    allowedRolesCsv: (e.allowedRoles ?? []).join(", "),
    requiresApproval: !!e.requiresApproval,
    perUserPerHour: e.perUserPerHour != null ? String(e.perUserPerHour) : "",
  };
}

function makeEmptyEndpointRow(): EndpointRow {
  return {
    name: "",
    displayName: "",
    description: "",
    method: "GET",
    path: "/",
    params: [],
    paramsJsonMode: false,
    paramsJson: "",
    paramsJsonError: null,
    allowedRolesCsv: "",
    requiresApproval: false,
    perUserPerHour: "",
  };
}

// Per-endpoint parameter editor. Two presentations of the same data:
// the friendlier row form (default) and a "View JSON" toggle that
// renders the generated JSON-Schema (+ paramIn) as an editable textarea
// for power users that want to author shapes the form doesn't expose.
function ParametersEditor({
  endpoint,
  idx,
  onChange,
}: {
  endpoint: EndpointRow;
  idx: number;
  onChange: (updater: (prev: EndpointRow) => EndpointRow) => void;
}) {
  // Path placeholders get pre-suggested as a hint so admins don't forget
  // to declare them. We don't auto-add the row though — we just nudge.
  const pathPlaceholders = Array.from(endpoint.path.matchAll(/\{([^}]+)\}/g)).map((m) => m[1]);
  const declaredNames = new Set(endpoint.params.map((p) => p.name.trim()).filter(Boolean));
  const missingPlaceholders = pathPlaceholders.filter((n) => !declaredNames.has(n));

  function setParam(pIdx: number, patch: Partial<ParamRow>) {
    onChange((prev) => {
      const params = [...prev.params];
      params[pIdx] = { ...params[pIdx], ...patch };
      return { ...prev, params };
    });
  }

  function addParam(seedName?: string) {
    onChange((prev) => ({
      ...prev,
      params: [
        ...prev.params,
        {
          name: seedName ?? "",
          type: "string",
          description: "",
          required: !!seedName,
          placement: seedName ? "path" : "auto",
          enumCsv: "",
          minimum: "",
          maximum: "",
        },
      ],
    }));
  }

  function removeParam(pIdx: number) {
    onChange((prev) => ({ ...prev, params: prev.params.filter((_, i) => i !== pIdx) }));
  }

  function toggleJsonView() {
    onChange((prev) => {
      if (prev.paramsJsonMode) {
        // Leaving JSON mode: try to parse what they typed back into rows.
        // On parse failure we surface the error inline and stay in JSON
        // mode so they don't lose work.
        const trimmed = prev.paramsJson.trim();
        if (!trimmed) {
          return { ...prev, paramsJsonMode: false, paramsJsonError: null, params: [] };
        }
        try {
          const parsed = JSON.parse(trimmed);
          return {
            ...prev,
            paramsJsonMode: false,
            paramsJsonError: null,
            params: rowsFromParsedSchema(parsed),
          };
        } catch (e: any) {
          return { ...prev, paramsJsonError: e?.message ?? "Invalid JSON" };
        }
      }
      // Entering JSON mode: render the current rows into JSON for them.
      return {
        ...prev,
        paramsJsonMode: true,
        paramsJson: previewJson(prev.params),
        paramsJsonError: null,
      };
    });
  }

  return (
    <div className="space-y-2" data-testid={`params-editor-${idx}`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs">Parameters</Label>
        <div className="flex items-center gap-2">
          {!endpoint.paramsJsonMode && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => addParam()}
              data-testid={`button-add-param-${idx}`}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add parameter
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={toggleJsonView}
            data-testid={`button-toggle-params-json-${idx}`}
          >
            {endpoint.paramsJsonMode ? (<><TableIcon className="w-3.5 h-3.5 mr-1" /> View as form</>) : (<><Code2 className="w-3.5 h-3.5 mr-1" /> View JSON</>)}
          </Button>
        </div>
      </div>

      {endpoint.paramsJsonMode ? (
        <div className="space-y-1">
          <Textarea
            className="font-mono text-xs min-h-[160px]"
            value={endpoint.paramsJson}
            onChange={(e) => onChange((prev) => ({ ...prev, paramsJson: e.target.value, paramsJsonError: null }))}
            data-testid={`textarea-endpoint-parameters-${idx}`}
          />
          {endpoint.paramsJsonError ? (
            <p className="text-xs text-rose-600" data-testid={`text-params-json-error-${idx}`}>{endpoint.paramsJsonError}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              JSON Schema with an optional <code>paramIn</code> map for placement overrides. Toggle back to "View as form" to import edits into rows.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {endpoint.params.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid={`text-no-params-${idx}`}>
              No parameters. The chatbot will call this endpoint with no inputs.
            </p>
          ) : (
            <div className="space-y-3">
              {endpoint.params.map((p, pIdx) => {
                const isNumeric = p.type === "number" || p.type === "integer";
                // Boolean params only accept the literal tokens "true" / "false" —
                // mirror the server's filter (and `buildParamsFromRows`) here so
                // the chips always reflect what will actually be persisted.
                const rawEnumPieces = (p.enumCsv ?? "")
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                const enumChips =
                  p.type === "boolean"
                    ? Array.from(
                        new Set(
                          rawEnumPieces.filter((s) => s === "true" || s === "false"),
                        ),
                      )
                    : rawEnumPieces;
                const enumSupported =
                  p.type === "string" ||
                  p.type === "number" ||
                  p.type === "integer" ||
                  p.type === "boolean";
                return (
                  <div
                    key={pIdx}
                    className="space-y-2 rounded-md border border-border/60 p-2"
                    data-testid={`row-param-${idx}-${pIdx}`}
                  >
                    <div className="grid gap-2 items-start grid-cols-1 sm:grid-cols-[1.2fr_110px_2fr_110px_90px_auto]">
                      <Input
                        placeholder="param_name"
                        value={p.name}
                        onChange={(e) => setParam(pIdx, { name: e.target.value })}
                        data-testid={`input-param-name-${idx}-${pIdx}`}
                      />
                      <Select value={p.type} onValueChange={(v) => setParam(pIdx, { type: v as ParamType })}>
                        <SelectTrigger data-testid={`select-param-type-${idx}-${pIdx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string" data-testid={`option-param-type-${idx}-${pIdx}-string`}>string</SelectItem>
                          <SelectItem value="number" data-testid={`option-param-type-${idx}-${pIdx}-number`}>number</SelectItem>
                          <SelectItem value="integer" data-testid={`option-param-type-${idx}-${pIdx}-integer`}>integer</SelectItem>
                          <SelectItem value="boolean" data-testid={`option-param-type-${idx}-${pIdx}-boolean`}>boolean</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="What the LLM should know about this parameter"
                        value={p.description}
                        onChange={(e) => setParam(pIdx, { description: e.target.value })}
                        data-testid={`input-param-description-${idx}-${pIdx}`}
                      />
                      <Select value={p.placement} onValueChange={(v) => setParam(pIdx, { placement: v as ParamPlacement })}>
                        <SelectTrigger data-testid={`select-param-placement-${idx}-${pIdx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto</SelectItem>
                          <SelectItem value="path">Path</SelectItem>
                          <SelectItem value="query">Query</SelectItem>
                          <SelectItem value="body">Body</SelectItem>
                          <SelectItem value="header">Header</SelectItem>
                        </SelectContent>
                      </Select>
                      <label htmlFor={`switch-param-required-${idx}-${pIdx}`} className="flex items-center gap-1 text-xs h-9">
                        <Switch
                          id={`switch-param-required-${idx}-${pIdx}`}
                          checked={p.required}
                          onCheckedChange={(s) => setParam(pIdx, { required: s })}
                          data-testid={`switch-param-required-${idx}-${pIdx}`}
                        />
                        Required
                      </label>
                      <IconButton
                        variant="ghost"
                        label={p.name ? `Remove parameter ${p.name}` : `Remove parameter #${pIdx + 1}`}
                        onClick={() => removeParam(pIdx)}
                        data-testid={`button-remove-param-${idx}-${pIdx}`}
                      >
                        <X className="w-4 h-4" aria-hidden="true" />
                      </IconButton>
                    </div>
                    {(enumSupported || isNumeric) && (
                      <div className="grid gap-2 items-start grid-cols-1 sm:grid-cols-[1.2fr_110px_2fr_110px_90px_auto]">
                        {/* The enum input mirrors the column layout above so the
                            "Allowed values" field aligns with the description column
                            and the min/max inputs land under "Placement" / "Required". */}
                        {enumSupported ? (
                          <>
                            <div className="hidden sm:block" />
                            <Label className="text-[11px] text-muted-foreground pt-2" htmlFor={`input-param-enum-${idx}-${pIdx}`}>Allowed values (enum)</Label>
                            <div className="space-y-1">
                              <Input
                                id={`input-param-enum-${idx}-${pIdx}`}
                                placeholder={
                                  p.type === "string"
                                    ? "open, in_progress, closed"
                                    : p.type === "boolean"
                                      ? "true, false"
                                      : "1, 2, 3"
                                }
                                value={p.enumCsv ?? ""}
                                onChange={(e) => setParam(pIdx, { enumCsv: e.target.value })}
                                data-testid={`input-param-enum-${idx}-${pIdx}`}
                              />
                              {enumChips.length > 0 && (
                                <div className="flex flex-wrap gap-1" data-testid={`chips-param-enum-${idx}-${pIdx}`}>
                                  {enumChips.map((c, ci) => (
                                    <Badge
                                      key={ci}
                                      variant="secondary"
                                      className="text-[10px] font-mono"
                                      data-testid={`chip-param-enum-${idx}-${pIdx}-${ci}`}
                                    >
                                      {c}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="hidden sm:block" />
                            <div className="hidden sm:block" />
                            <div className="hidden sm:block" />
                          </>
                        )}
                        {isNumeric ? (
                          <>
                            <Input
                              type="number"
                              placeholder="min"
                              value={p.minimum ?? ""}
                              onChange={(e) => setParam(pIdx, { minimum: e.target.value })}
                              data-testid={`input-param-minimum-${idx}-${pIdx}`}
                            />
                            <Input
                              type="number"
                              placeholder="max"
                              value={p.maximum ?? ""}
                              onChange={(e) => setParam(pIdx, { maximum: e.target.value })}
                              data-testid={`input-param-maximum-${idx}-${pIdx}`}
                            />
                          </>
                        ) : (
                          <>
                            <div className="hidden sm:block" />
                            <div className="hidden sm:block" />
                          </>
                        )}
                        <div className="hidden sm:block" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {missingPlaceholders.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-amber-700 dark:text-amber-300" data-testid={`hint-missing-path-params-${idx}`}>
              <span>Path uses placeholders not declared yet:</span>
              {missingPlaceholders.map((n) => (
                <Button
                  key={n}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => addParam(n)}
                  data-testid={`button-add-missing-param-${idx}-${n}`}
                >
                  + {n}
                </Button>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            "Auto" placement uses path for <code>{"{name}"}</code> placeholders, query for GET/DELETE, JSON body otherwise.
          </p>
        </div>
      )}
    </div>
  );
}

function CustomCard({ existing }: { existing: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const initial = existing.custom;
  const [name, setName] = useState(existing.name);
  const [baseUrl, setBaseUrl] = useState(existing.instanceUrl ?? initial?.baseUrl ?? "");
  const [authType, setAuthType] = useState<AuthType>(initial?.authType ?? "none");
  const [headerName, setHeaderName] = useState(initial?.authHeaderName ?? "");
  const [headerValue, setHeaderValue] = useState("");
  const [bearerToken, setBearerToken] = useState("");
  const [basicUsername, setBasicUsername] = useState(initial?.authUsername ?? "");
  const [basicPassword, setBasicPassword] = useState("");
  const [testPath, setTestPath] = useState(initial?.testPath ?? "/");
  const [successStatuses, setSuccessStatuses] = useState<string>((initial?.successStatuses ?? []).join(", "));
  const [headers, setHeaders] = useState<HeaderRow[]>(() =>
    (initial?.headers ?? []).map((h) => ({
      name: h.name,
      value: h.secret ? "" : (h.value ?? ""),
      secret: h.secret,
      saved: h.secret,
    })),
  );
  const [endpoints, setEndpoints] = useState<EndpointRow[]>(() =>
    (initial?.endpoints ?? []).map(endpointDtoToRow),
  );

  // When we navigate between integration tabs, hydrate the form from the
  // freshest server state so secret placeholders stay accurate.
  useEffect(() => {
    if (!existing) return;
    setName(existing.name);
    setBaseUrl(existing.instanceUrl ?? existing.custom?.baseUrl ?? "");
    if (existing.custom) {
      setAuthType(existing.custom.authType);
      setHeaderName(existing.custom.authHeaderName ?? "");
      setBasicUsername(existing.custom.authUsername ?? "");
      setTestPath(existing.custom.testPath ?? "/");
      setSuccessStatuses((existing.custom.successStatuses ?? []).join(", "));
      setHeaders(
        (existing.custom.headers ?? []).map((h) => ({
          name: h.name,
          value: h.secret ? "" : (h.value ?? ""),
          secret: h.secret,
          saved: h.secret,
        })),
      );
      setEndpoints((existing.custom.endpoints ?? []).map(endpointDtoToRow));
    }
  }, [existing.id]);

  const saveMut = useMutation({
    mutationFn: async () => {
      // Build the JSON-Schema + paramIn payload from the row editor.
      // When the admin is in "View JSON" mode for a given endpoint, that
      // textarea is the source of truth so they can author shapes the
      // form doesn't expose (e.g. enums); otherwise the rows win.
      const endpointsPayload = endpoints.map((ep) => {
        let parameters: any;
        let paramIn: any;
        if (ep.paramsJsonMode) {
          // Source of truth is the textarea — `buildParamsFromJsonMode`
          // peels `paramIn` back out of the unified JSON document we
          // render in "View JSON" mode and surfaces invalid JSON as a
          // labelled error so the save aborts with a per-endpoint
          // message instead of sending garbage to the server. See
          // `client/src/pages/__tests__/integrations.helpers.test.ts`
          // for the locked-in behaviour.
          const built = buildParamsFromJsonMode(ep.paramsJson, ep.name);
          parameters = built.parameters;
          paramIn = built.paramIn;
        } else {
          const built = buildParamsFromRows(ep.params);
          parameters = built.parameters;
          paramIn = built.paramIn;
        }
        const allowedRoles = ep.allowedRolesCsv
          .split(/[,\s]+/)
          .map((r) => r.trim())
          .filter(Boolean);
        const perUserPerHour = ep.perUserPerHour.trim() === ""
          ? null
          : Number(ep.perUserPerHour);
        return {
          name: ep.name,
          displayName: ep.displayName || ep.name,
          description: ep.description,
          method: ep.method,
          path: ep.path,
          parameters,
          paramIn,
          allowedRoles: allowedRoles.length ? allowedRoles : undefined,
          requiresApproval: ep.requiresApproval,
          perUserPerHour: Number.isFinite(perUserPerHour as number) ? perUserPerHour : null,
        };
      });
      return apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
        type: existing.type, // `custom:<slug>` → triggers the update branch
        name,
        instanceUrl: baseUrl,
        config: {
          authType,
          authConfig:
            authType === "api_key" ? { headerName, headerValue } :
            authType === "bearer" ? { token: bearerToken } :
            authType === "basic" ? { username: basicUsername, password: basicPassword } :
            {},
          headers: headers
            .filter((h) => h.name.trim().length > 0)
            .map((h) => ({ name: h.name, value: h.value, secret: h.secret })),
          testPath,
          successStatuses: successStatuses
            .split(/[,\s]+/)
            .map((s) => Number(s))
            .filter((n) => Number.isInteger(n) && n >= 100 && n < 600),
          endpoints: endpointsPayload,
        },
      });
    },
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, name);
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      // The server saves the integration row even if projecting endpoints
      // onto the chatbot tool registry fails, so we surface that as a
      // separate destructive toast rather than letting the admin assume
      // their tools went live.
      if (json?.toolSyncWarning) {
        toast({ title: "Tool list out of date", description: String(json.toolSyncWarning), variant: "destructive" });
      }
      setHeaderValue(""); setBearerToken(""); setBasicPassword("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/integrations/${existing.id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, name);
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? `${name} responded successfully.` : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async () => apiRequest("DELETE", `/api/integrations/${existing.id}`),
    onSuccess: () => {
      toast({ title: "Integration removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const slugBadge = existing.type.replace(/^custom:/, "");

  return (
    <Card data-testid={`card-integration-${existing.type}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              {name}
              <Badge variant="outline" className="text-xs font-normal" data-testid={`badge-slug-${existing.type}`}>{slugBadge}</Badge>
              <StatusPill status={existing.status} />
            </CardTitle>
            <CardDescription>Custom REST integration</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm" data-testid={`button-view-audit-${existing.type}`}>
              <Link href={`/admin/audit/tools?integration=${encodeURIComponent(existing.type)}`}>
                <ScrollText className="w-4 h-4 mr-1" /> View audit log
              </Link>
            </Button>
            <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate()} data-testid={`button-test-connection-${existing.type}`}>
              <RefreshCcw className="w-4 h-4 mr-1" /> Test
            </Button>
            <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => { if (confirm(`Remove "${existing.name}"?`)) deleteMut.mutate(); }} data-testid={`button-delete-integration-${existing.type}`}>
              <Trash2 className="w-4 h-4 mr-1" /> Remove
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-muted-foreground">
          Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
          {existing.lastError ? (
            <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid={`text-last-error-${existing.type}`}>
              {existing.lastError}
            </span>
          ) : null}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor={`cu-name-${existing.id}`}>Display name</Label>
            <Input id={`cu-name-${existing.id}`} value={name} onChange={(e) => setName(e.target.value)} data-testid={`input-name-${existing.type}`} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`cu-url-${existing.id}`}>Base URL</Label>
            <Input id={`cu-url-${existing.id}`} placeholder="https://api.example.com" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} data-testid={`input-base-url-${existing.type}`} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`cu-auth-${existing.id}`}>Authentication</Label>
            <Select value={authType} onValueChange={(v) => setAuthType(v as AuthType)}>
              <SelectTrigger id={`cu-auth-${existing.id}`} data-testid={`select-auth-type-${existing.type}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="api_key">API key (header)</SelectItem>
                <SelectItem value="bearer">Bearer token</SelectItem>
                <SelectItem value="basic">Basic auth</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`cu-tp-${existing.id}`}>Test path</Label>
            <Input id={`cu-tp-${existing.id}`} placeholder="/" value={testPath} onChange={(e) => setTestPath(e.target.value)} data-testid={`input-test-path-${existing.type}`} />
          </div>
        </div>
        {authType === "api_key" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor={`input-header-name-${existing.type}`}>Header name</Label>
              <Input id={`input-header-name-${existing.type}`} placeholder="X-API-Key" value={headerName} onChange={(e) => setHeaderName(e.target.value)} data-testid={`input-header-name-${existing.type}`} />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`input-header-value-${existing.type}`}>Header value</Label>
              <Input id={`input-header-value-${existing.type}`} type="password" placeholder={existing.configured ? "•••••• (saved)" : ""} value={headerValue} onChange={(e) => setHeaderValue(e.target.value)} data-testid={`input-header-value-${existing.type}`} />
            </div>
          </div>
        )}
        {authType === "bearer" && (
          <div className="space-y-1">
            <Label htmlFor={`input-bearer-token-${existing.type}`}>Bearer token</Label>
            <Input id={`input-bearer-token-${existing.type}`} type="password" placeholder={existing.configured ? "•••••• (saved)" : ""} value={bearerToken} onChange={(e) => setBearerToken(e.target.value)} data-testid={`input-bearer-token-${existing.type}`} />
          </div>
        )}
        {authType === "basic" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor={`input-basic-username-${existing.type}`}>Username</Label>
              <Input id={`input-basic-username-${existing.type}`} value={basicUsername} onChange={(e) => setBasicUsername(e.target.value)} data-testid={`input-basic-username-${existing.type}`} />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`input-basic-password-${existing.type}`}>Password</Label>
              <Input id={`input-basic-password-${existing.type}`} type="password" placeholder={existing.configured ? "•••••• (saved)" : ""} value={basicPassword} onChange={(e) => setBasicPassword(e.target.value)} data-testid={`input-basic-password-${existing.type}`} />
            </div>
          </div>
        )}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Custom headers</Label>
            <Button variant="ghost" size="sm" onClick={() => setHeaders([...headers, { name: "", value: "", secret: false }])} data-testid={`button-add-header-${existing.type}`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add header
            </Button>
          </div>
          {headers.length === 0 && <p className="text-xs text-muted-foreground">No custom headers.</p>}
          {headers.map((h, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
              <Input placeholder="Header name" value={h.name} onChange={(e) => { const n = [...headers]; n[idx] = { ...n[idx], name: e.target.value }; setHeaders(n); }} data-testid={`input-custom-header-name-${idx}`} />
              <Input
                type={h.secret ? "password" : "text"}
                placeholder={h.saved ? "•••••• (saved)" : "Header value"}
                value={h.value}
                onChange={(e) => { const n = [...headers]; n[idx] = { ...n[idx], value: e.target.value, saved: false }; setHeaders(n); }}
                data-testid={`input-custom-header-value-${idx}`}
              />
              <label htmlFor={`switch-custom-header-secret-${idx}`} className="flex items-center gap-1 text-xs">
                <Switch id={`switch-custom-header-secret-${idx}`} checked={h.secret} onCheckedChange={(s) => { const n = [...headers]; n[idx] = { ...n[idx], secret: s }; setHeaders(n); }} data-testid={`switch-custom-header-secret-${idx}`} />
                Secret
              </label>
              <IconButton variant="ghost" label={h.name ? `Remove header ${h.name}` : `Remove header #${idx + 1}`} onClick={() => setHeaders(headers.filter((_, i) => i !== idx))} data-testid={`button-remove-header-${idx}`}>
                <X className="w-4 h-4" aria-hidden="true" />
              </IconButton>
            </div>
          ))}
        </div>
        <div className="space-y-1">
          <Label htmlFor={`cu-ss-${existing.id}`}>Success status codes (optional)</Label>
          <Input id={`cu-ss-${existing.id}`} placeholder="200, 204" value={successStatuses} onChange={(e) => setSuccessStatuses(e.target.value)} data-testid={`input-success-statuses-${existing.type}`} />
          <p className="text-xs text-muted-foreground">Comma-separated. Leave blank to accept any 2xx.</p>
        </div>

        {/* ── Endpoints ────────────────────────────────────────────────── */}
        {/* Each endpoint becomes a chatbot tool the LLM can call. The tool
            shows up on Chatbots → (bot) → Tools so admins can enable /
            disable per-bot, and inherits the per-tool RBAC + rate-limit +
            approval policy from the values entered here on first save. */}
        <div className="space-y-2 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Chatbot endpoints</Label>
              <p className="text-xs text-muted-foreground">
                Define the actions chatbots may invoke on this integration. Each becomes a tool named <code>custom__{existing.type.replace(/^custom:/, "")}__&lt;name&gt;</code>.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEndpoints([...endpoints, makeEmptyEndpointRow()])}
              data-testid={`button-add-endpoint-${existing.type}`}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add endpoint
            </Button>
          </div>
          {endpoints.length === 0 && (
            <p className="text-xs text-muted-foreground">No endpoints. Add one to let chatbots call this integration.</p>
          )}
          {endpoints.map((ep, idx) => (
            <div key={idx} className="rounded-md border p-3 space-y-2 bg-muted/30" data-testid={`row-endpoint-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Endpoint #{idx + 1}</span>
                <IconButton variant="ghost" label={ep.displayName || ep.name ? `Remove endpoint ${ep.displayName || ep.name}` : `Remove endpoint #${idx + 1}`} onClick={() => setEndpoints(endpoints.filter((_, i) => i !== idx))} data-testid={`button-remove-endpoint-${idx}`}>
                  <X className="w-4 h-4" aria-hidden="true" />
                </IconButton>
              </div>
              <div className="grid sm:grid-cols-[1fr_1fr_140px] gap-2">
                <div className="space-y-1">
                  <Label className="text-xs" htmlFor={`input-endpoint-name-${idx}`}>Tool name</Label>
                  <Input id={`input-endpoint-name-${idx}`} placeholder="search_orders" value={ep.name} onChange={(e) => { const n = [...endpoints]; n[idx] = { ...n[idx], name: e.target.value }; setEndpoints(n); }} data-testid={`input-endpoint-name-${idx}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs" htmlFor={`input-endpoint-display-${idx}`}>Display name</Label>
                  <Input id={`input-endpoint-display-${idx}`} placeholder="Search Orders" value={ep.displayName} onChange={(e) => { const n = [...endpoints]; n[idx] = { ...n[idx], displayName: e.target.value }; setEndpoints(n); }} data-testid={`input-endpoint-display-${idx}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs" htmlFor={`select-endpoint-method-${idx}`}>Method</Label>
                  <Select value={ep.method} onValueChange={(v) => { const n = [...endpoints]; n[idx] = { ...n[idx], method: v as EndpointRow["method"] }; setEndpoints(n); }}>
                    <SelectTrigger id={`select-endpoint-method-${idx}`} data-testid={`select-endpoint-method-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                      <SelectItem value="PATCH">PATCH</SelectItem>
                      <SelectItem value="DELETE">DELETE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs" htmlFor={`input-endpoint-path-${idx}`}>Path</Label>
                <Input id={`input-endpoint-path-${idx}`} placeholder="/orders/{orderId}" value={ep.path} onChange={(e) => { const n = [...endpoints]; n[idx] = { ...n[idx], path: e.target.value }; setEndpoints(n); }} data-testid={`input-endpoint-path-${idx}`} />
                <p className="text-xs text-muted-foreground">Use <code>{"{name}"}</code> for path params. Other params go to query (GET/DELETE) or JSON body.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs" htmlFor={`input-endpoint-description-${idx}`}>Description (the LLM sees this)</Label>
                <Input id={`input-endpoint-description-${idx}`} placeholder="Looks up an order by ID and returns status, items, and shipping info." value={ep.description} onChange={(e) => { const n = [...endpoints]; n[idx] = { ...n[idx], description: e.target.value }; setEndpoints(n); }} data-testid={`input-endpoint-description-${idx}`} />
              </div>
              <ParametersEditor
                endpoint={ep}
                idx={idx}
                onChange={(updater) => {
                  const n = [...endpoints];
                  n[idx] = updater(n[idx]);
                  setEndpoints(n);
                }}
              />
              <div className="grid sm:grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs" htmlFor={`input-endpoint-roles-${idx}`}>Allowed roles</Label>
                  <Input id={`input-endpoint-roles-${idx}`} placeholder="employee, manager" value={ep.allowedRolesCsv} onChange={(e) => { const n = [...endpoints]; n[idx] = { ...n[idx], allowedRolesCsv: e.target.value }; setEndpoints(n); }} data-testid={`input-endpoint-roles-${idx}`} />
                  <p className="text-xs text-muted-foreground">Comma-separated. Leave blank for all.</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs" htmlFor={`input-endpoint-rate-${idx}`}>Per-user/hour limit</Label>
                  <Input id={`input-endpoint-rate-${idx}`} type="number" min={0} placeholder="∞" value={ep.perUserPerHour} onChange={(e) => { const n = [...endpoints]; n[idx] = { ...n[idx], perUserPerHour: e.target.value }; setEndpoints(n); }} data-testid={`input-endpoint-rate-${idx}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs" htmlFor={`switch-endpoint-approval-${idx}`}>Require manager approval</Label>
                  <div className="h-9 flex items-center">
                    <Switch id={`switch-endpoint-approval-${idx}`} checked={ep.requiresApproval} onCheckedChange={(s) => { const n = [...endpoints]; n[idx] = { ...n[idx], requiresApproval: s }; setEndpoints(n); }} data-testid={`switch-endpoint-approval-${idx}`} />
                  </div>
                </div>
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Initial values seed the matching <strong>Tool</strong> rows. Once created, edit RBAC and limits from <strong>Chatbots → Tools</strong>; subsequent saves here only refresh the metadata + parameter schema.
          </p>
        </div>

        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={saveMut.isPending || !name || !baseUrl}
            onClick={() => saveMut.mutate()}
            data-testid={`button-save-integration-${existing.type}`}
          >
            {saveMut.isPending ? "Saving…" : "Update connection"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Twilio card — telephony / messaging. Account SID + Auth Token + a
// from-number. Mocked by default so the org admin can wire the card up
// before they have real Twilio creds; flipping the "Use mock" switch
// off pushes the live API on Save & test.
// ──────────────────────────────────────────────────────────────────────

function TwilioCard({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [name, setName] = useState(existing?.name ?? "Twilio");
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [voiceWebhookUrl, setVoiceWebhookUrl] = useState("");
  const [mock, setMock] = useState(true);

  useEffect(() => {
    if (!existing) return;
    setName((prev) => (prev && prev !== "Twilio" ? prev : existing.name || "Twilio"));
  }, [existing?.id]);

  const saveMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "twilio",
      name,
      config: {
        mock,
        accountSid: accountSid.trim(),
        authToken,
        phoneNumber: phoneNumber.trim(),
        voiceWebhookUrl: voiceWebhookUrl.trim() || undefined,
      },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Twilio");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setAuthToken("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Twilio");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "Twilio responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Integration removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setName("Twilio"); setAccountSid(""); setAuthToken(""); setPhoneNumber(""); setVoiceWebhookUrl(""); setMock(true);
    },
  });

  const realRequiredOk = mock || (accountSid.trim().length > 0 && (existing?.configured || authToken.length > 0));

  return (
    <Card data-testid="card-integration-twilio">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">Twilio
              {existing && <StatusPill status={existing.status} />}
              {mock && <Badge variant="secondary" data-testid="badge-mock-twilio">Mock</Badge>}
            </CardTitle>
            <CardDescription>
              Voice + SMS for live-agent calls and outbound notifications. Pre-seeded with a sandbox config — swap in real credentials when ready.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-twilio">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-twilio">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-twilio">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-twilio">{existing.lastError}</span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="tw-name">Display name</Label>
            <Input id="tw-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-twilio" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tw-from">From number (E.164)</Label>
            <Input id="tw-from" placeholder="+15555550123" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} data-testid="input-phone-twilio" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tw-sid">Account SID</Label>
            <Input id="tw-sid" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" value={accountSid} onChange={(e) => setAccountSid(e.target.value)} data-testid="input-account-sid-twilio" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tw-tok">Auth Token</Label>
            <Input id="tw-tok" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={authToken} onChange={(e) => setAuthToken(e.target.value)} data-testid="input-auth-token-twilio" />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="tw-vwh">Voice webhook URL (optional)</Label>
            <Input id="tw-vwh" placeholder="https://example.com/twilio/voice" value={voiceWebhookUrl} onChange={(e) => setVoiceWebhookUrl(e.target.value)} data-testid="input-voice-webhook-twilio" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch id="tw-mock" checked={mock} onCheckedChange={setMock} data-testid="switch-mock-twilio" />
          <Label htmlFor="tw-mock" className="cursor-pointer">
            Use mock mode (no outbound API calls — perfect for demo / setup)
          </Label>
        </div>
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={saveMut.isPending || !realRequiredOk}
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-twilio"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Microsoft Dynamics 365 card — Dataverse via AAD client-credentials.
// Same mock-by-default UX as Twilio.
// ──────────────────────────────────────────────────────────────────────

function Dynamics365Card({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [name, setName] = useState(existing?.name ?? "Microsoft Dynamics 365");
  const [environmentUrl, setEnvironmentUrl] = useState(existing?.instanceUrl ?? "");
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [defaultEntity, setDefaultEntity] = useState("incident");
  const [mock, setMock] = useState(true);

  useEffect(() => {
    if (!existing) return;
    setName((prev) => (prev && prev !== "Microsoft Dynamics 365" ? prev : existing.name || "Microsoft Dynamics 365"));
    setEnvironmentUrl((prev) => prev || existing.instanceUrl || "");
  }, [existing?.id]);

  const saveMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "dynamics365",
      name,
      instanceUrl: environmentUrl.trim(),
      config: {
        mock,
        tenantId: tenantId.trim(),
        clientId: clientId.trim(),
        clientSecret,
        environmentUrl: environmentUrl.trim(),
        defaultEntity: defaultEntity.trim() || "incident",
      },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Dynamics 365");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setClientSecret("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Dynamics 365");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "Dynamics 365 responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Integration removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setName("Microsoft Dynamics 365"); setEnvironmentUrl(""); setTenantId(""); setClientId(""); setClientSecret(""); setDefaultEntity("incident"); setMock(true);
    },
  });

  const realRequiredOk = mock || (
    environmentUrl.trim().length > 0 && tenantId.trim().length > 0 && clientId.trim().length > 0 &&
    (existing?.configured || clientSecret.length > 0)
  );

  return (
    <Card data-testid="card-integration-dynamics365">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">Microsoft Dynamics 365
              {existing && <StatusPill status={existing.status} />}
              {mock && <Badge variant="secondary" data-testid="badge-mock-dynamics365">Mock</Badge>}
            </CardTitle>
            <CardDescription>
              Open and manage Dataverse cases / incidents from chat. Pre-seeded with a sandbox config — swap in real Azure AD credentials when ready.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-dynamics365">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-dynamics365">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-dynamics365">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-dynamics365">{existing.lastError}</span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="d365-name">Display name</Label>
            <Input id="d365-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-dynamics365" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="d365-env">Environment URL</Label>
            <Input id="d365-env" placeholder="https://acme.crm.dynamics.com" value={environmentUrl} onChange={(e) => setEnvironmentUrl(e.target.value)} data-testid="input-environment-url-dynamics365" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="d365-tenant">Azure AD Tenant ID</Label>
            <Input id="d365-tenant" placeholder="00000000-0000-0000-0000-000000000000" value={tenantId} onChange={(e) => setTenantId(e.target.value)} data-testid="input-tenant-id-dynamics365" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="d365-cid">App Client ID</Label>
            <Input id="d365-cid" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="input-client-id-dynamics365" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="d365-cs">App Client Secret</Label>
            <Input id="d365-cs" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} data-testid="input-client-secret-dynamics365" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="d365-ent">Default entity</Label>
            <Input id="d365-ent" placeholder="incident" value={defaultEntity} onChange={(e) => setDefaultEntity(e.target.value)} data-testid="input-default-entity-dynamics365" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch id="d365-mock" checked={mock} onCheckedChange={setMock} data-testid="switch-mock-dynamics365" />
          <Label htmlFor="d365-mock" className="cursor-pointer">
            Use mock mode (no outbound API calls — perfect for demo / setup)
          </Label>
        </div>
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={saveMut.isPending || !realRequiredOk}
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-dynamics365"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Microsoft 365 (Microsoft Graph) card — AAD client-credentials.
// Calendar (events / free-busy) + Entra ID directory lookups.
// ──────────────────────────────────────────────────────────────────────

function Microsoft365Card({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [name, setName] = useState(existing?.name ?? "Microsoft 365");
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [mock, setMock] = useState(true);

  useEffect(() => {
    if (!existing) return;
    setName((prev) => (prev && prev !== "Microsoft 365" ? prev : existing.name || "Microsoft 365"));
    if (typeof existing.mock === "boolean") setMock(existing.mock);
  }, [existing?.id]);

  const saveMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "m365",
      name,
      config: { mock, tenantId: tenantId.trim(), clientId: clientId.trim(), clientSecret },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Microsoft 365");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setClientSecret("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Microsoft 365");
      toast({ title: verdict.ok ? "Token verified" : verdict.title, description: verdict.ok ? "Entra credentials were accepted. SharePoint site access has not been tested." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Integration removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setName("Microsoft 365"); setTenantId(""); setClientId(""); setClientSecret(""); setMock(true);
    },
  });

  const realRequiredOk = mock || (
    tenantId.trim().length > 0 && clientId.trim().length > 0 && (existing?.configured || clientSecret.length > 0)
  );

  return (
    <Card data-testid="card-integration-m365">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">Microsoft 365
              {existing && <StatusPill status={existing.status === "token_expired" && existing.lastError?.includes("graph_call_failed:403") ? "permission_denied" : existing.status} />}
              {mock && <Badge variant="secondary" data-testid="badge-mock-m365">Mock</Badge>}
            </CardTitle>
            <CardDescription>
              Connect SharePoint knowledge sources using an Entra application. Token verification and selected-site access are tested separately.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-m365">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test token
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-m365">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-m365">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-m365">{existing.lastError}</span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="m365-name">Display name</Label>
            <Input id="m365-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-m365" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="m365-tenant">Directory (tenant) ID</Label>
            <Input id="m365-tenant" placeholder="00000000-0000-0000-0000-000000000000" value={tenantId} onChange={(e) => setTenantId(e.target.value)} data-testid="input-tenant-id-m365" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="m365-cid">Application (client) ID</Label>
            <Input id="m365-cid" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="input-client-id-m365" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="m365-cs">Client secret</Label>
            <Input id="m365-cs" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} data-testid="input-client-secret-m365" />
          </div>
        </div>
        <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="text-m365-help">
          <strong>SharePoint setup:</strong> use Microsoft Graph application <code>Sites.Selected</code>,
          grant admin consent, and grant this application Read access on the selected site.
          Directory and calendar permissions are not required for SharePoint testing.
          The token test verifies credentials only; it does not prove site or document access.
        </div>
        <div className="flex items-center gap-3">
          <Switch id="m365-mock" checked={mock} onCheckedChange={setMock} data-testid="switch-mock-m365" />
          <Label htmlFor="m365-mock" className="cursor-pointer">
            Use mock mode (no outbound API calls — perfect for demo / setup)
          </Label>
        </div>
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={saveMut.isPending || !realRequiredOk}
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-m365"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
        <SharePointAccessTest chatbotId={chatbotId}
          available={!!existing?.configured && !mock && !["pending_approval", "disabled", "rejected", "inactive"].includes(existing.status ?? "")} />
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Salesforce card — connected-app OAuth 2.0 client-credentials.
// Contact/lead lookup + case creation.
// ──────────────────────────────────────────────────────────────────────

function SalesforceCard({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [name, setName] = useState(existing?.name ?? "Salesforce");
  const [instanceUrl, setInstanceUrl] = useState(existing?.instanceUrl ?? "");
  const [consumerKey, setConsumerKey] = useState("");
  const [consumerSecret, setConsumerSecret] = useState("");
  const [mock, setMock] = useState(true);

  useEffect(() => {
    if (!existing) return;
    setName((prev) => (prev && prev !== "Salesforce" ? prev : existing.name || "Salesforce"));
    setInstanceUrl((prev) => prev || existing.instanceUrl || "");
    if (typeof existing.mock === "boolean") setMock(existing.mock);
  }, [existing?.id]);

  const saveMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "salesforce",
      name,
      instanceUrl: instanceUrl.trim(),
      config: { mock, instanceUrl: instanceUrl.trim(), consumerKey: consumerKey.trim(), consumerSecret },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Salesforce");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setConsumerSecret("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Salesforce");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "Salesforce responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Integration removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setName("Salesforce"); setInstanceUrl(""); setConsumerKey(""); setConsumerSecret(""); setMock(true);
    },
  });

  const realRequiredOk = mock || (
    instanceUrl.trim().length > 0 && consumerKey.trim().length > 0 && (existing?.configured || consumerSecret.length > 0)
  );

  return (
    <Card data-testid="card-integration-salesforce">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">Salesforce
              {existing && <StatusPill status={existing.status} />}
              {mock && <Badge variant="secondary" data-testid="badge-mock-salesforce">Mock</Badge>}
            </CardTitle>
            <CardDescription>
              Look up contacts and leads, and open cases / inquiries from chat. Uses a connected app with the OAuth client-credentials flow.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-salesforce">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-salesforce">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-salesforce">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-salesforce">{existing.lastError}</span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="sf-name">Display name</Label>
            <Input id="sf-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-salesforce" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sf-url">My Domain URL</Label>
            <Input id="sf-url" placeholder="https://acme.my.salesforce.com" value={instanceUrl} onChange={(e) => setInstanceUrl(e.target.value)} data-testid="input-instance-url-salesforce" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sf-key">Consumer key</Label>
            <Input id="sf-key" value={consumerKey} onChange={(e) => setConsumerKey(e.target.value)} data-testid="input-consumer-key-salesforce" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sf-secret">Consumer secret</Label>
            <Input id="sf-secret" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={consumerSecret} onChange={(e) => setConsumerSecret(e.target.value)} data-testid="input-consumer-secret-salesforce" />
          </div>
        </div>
        <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="text-salesforce-help">
          <strong>Set-up:</strong> in Setup → App Manager, create a connected app with OAuth enabled, the
          <em> Client Credentials Flow</em> turned on, and a run-as user that has API access. Paste the My Domain URL,
          consumer key and consumer secret above.
        </div>
        <div className="flex items-center gap-3">
          <Switch id="sf-mock" checked={mock} onCheckedChange={setMock} data-testid="switch-mock-salesforce" />
          <Label htmlFor="sf-mock" className="cursor-pointer">
            Use mock mode (no outbound API calls — perfect for demo / setup)
          </Label>
        </div>
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={saveMut.isPending || !realRequiredOk}
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-salesforce"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Slate (Technolutions) card — Basic auth against the institution's Slate
// instance. Applicant status lookup + inquiry submission.
// ──────────────────────────────────────────────────────────────────────

function SlateCard({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [name, setName] = useState(existing?.name ?? "Slate");
  const [instanceUrl, setInstanceUrl] = useState(existing?.instanceUrl ?? "");
  const [apiUser, setApiUser] = useState("");
  const [apiPassword, setApiPassword] = useState("");
  const [applicantQueryUrl, setApplicantQueryUrl] = useState("");
  const [inquiryUrl, setInquiryUrl] = useState("");
  const [mock, setMock] = useState(true);
  const [sampleApplicants, setSampleApplicants] = useState<SlateSampleApplicant[]>([]);

  useEffect(() => {
    if (!existing) return;
    setName((prev) => (prev && prev !== "Slate" ? prev : existing.name || "Slate"));
    setInstanceUrl((prev) => prev || existing.instanceUrl || "");
    if (typeof existing.mock === "boolean") setMock(existing.mock);
    if (Array.isArray(existing.sampleApplicants)) {
      setSampleApplicants(existing.sampleApplicants.map((a) => ({
        name: a.name ?? "", email: a.email ?? "", campus: a.campus ?? "",
        program: a.program ?? "", status: a.status ?? "", decision: a.decision ?? "",
      })));
    }
  }, [existing?.id]);

  const blankApplicant = (): SlateSampleApplicant => ({ name: "", email: "", campus: "", program: "", status: "", decision: "" });
  const addApplicant = () => setSampleApplicants((rows) => [...rows, blankApplicant()]);
  const removeApplicant = (i: number) => setSampleApplicants((rows) => rows.filter((_, idx) => idx !== i));
  const updateApplicant = (i: number, field: keyof SlateSampleApplicant, value: string) =>
    setSampleApplicants((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const saveMut = useMutation({
    mutationFn: async () => {
      // Keep only rows with both a name and an email (the tool requires both).
      // Send an explicit empty array when the admin has cleared everything so
      // the server reverts to the built-in SUNY defaults.
      const cleaned = sampleApplicants
        .map((a) => ({
          name: a.name.trim(), email: a.email.trim(), campus: a.campus.trim(),
          program: a.program.trim(), status: a.status.trim(), decision: a.decision.trim(),
        }))
        .filter((a) => a.name.length > 0 && a.email.length > 0);
      return apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
        type: "slate",
        name,
        instanceUrl: instanceUrl.trim(),
        config: {
          mock,
          instanceUrl: instanceUrl.trim(),
          apiUser: apiUser.trim(),
          apiPassword,
          applicantQueryUrl: applicantQueryUrl.trim() || undefined,
          inquiryUrl: inquiryUrl.trim() || undefined,
          sampleApplicants: cleaned,
        },
      });
    },
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Slate");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setApiPassword("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Slate");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "Slate responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Integration removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setName("Slate"); setInstanceUrl(""); setApiUser(""); setApiPassword(""); setApplicantQueryUrl(""); setInquiryUrl(""); setMock(true); setSampleApplicants([]);
    },
  });

  const realRequiredOk = mock || (
    instanceUrl.trim().length > 0 && apiUser.trim().length > 0 && (existing?.configured || apiPassword.length > 0)
  );

  return (
    <Card data-testid="card-integration-slate">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">Slate
              {existing && <StatusPill status={existing.status} />}
              {mock && <Badge variant="secondary" data-testid="badge-mock-slate">Mock</Badge>}
            </CardTitle>
            <CardDescription>
              Look up applicant / application status and submit prospective-student inquiries to your Technolutions Slate instance.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-slate">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-slate">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-slate">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-slate">{existing.lastError}</span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="slate-name">Display name</Label>
            <Input id="slate-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-slate" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="slate-url">Slate instance URL</Label>
            <Input id="slate-url" placeholder="https://apply.example.edu" value={instanceUrl} onChange={(e) => setInstanceUrl(e.target.value)} data-testid="input-instance-url-slate" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="slate-user">API user</Label>
            <Input id="slate-user" value={apiUser} onChange={(e) => setApiUser(e.target.value)} data-testid="input-api-user-slate" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="slate-pass">API password</Label>
            <Input id="slate-pass" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={apiPassword} onChange={(e) => setApiPassword(e.target.value)} data-testid="input-api-password-slate" />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="slate-query">Applicant Query web-service URL</Label>
            <Input id="slate-query" placeholder="https://apply.example.edu/manage/service/query?id=…&cmd=service&output=json" value={applicantQueryUrl} onChange={(e) => setApplicantQueryUrl(e.target.value)} data-testid="input-applicant-query-url-slate" />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="slate-inquiry">Inquiry form endpoint URL (optional)</Label>
            <Input id="slate-inquiry" placeholder="https://apply.example.edu/register/inquiry" value={inquiryUrl} onChange={(e) => setInquiryUrl(e.target.value)} data-testid="input-inquiry-url-slate" />
          </div>
        </div>
        <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="text-slate-help">
          <strong>Set-up:</strong> in Slate, create a service account user with Basic-auth access. Build a query, expose
          it as a web service (JSON) that filters by <code>email</code>, and paste its URL as the Applicant Query URL —
          this is also used to verify the connection. Optionally add an inquiry form endpoint to enable inquiry submission.
        </div>
        <div className="flex items-center gap-3">
          <Switch id="slate-mock" checked={mock} onCheckedChange={setMock} data-testid="switch-mock-slate" />
          <Label htmlFor="slate-mock" className="cursor-pointer">
            Use mock mode (no outbound API calls — perfect for demo / setup)
          </Label>
        </div>
        {mock && (
          <div className="space-y-3 rounded-md border p-3" data-testid="section-sample-applicants-slate">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Label className="text-sm font-medium">Sample applicants</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Tailor the demo records the <code>slate_application_status</code> tool returns in mock mode.
                  Name and email are required for a row to be used. Clear all rows to use the built-in SUNY defaults.
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addApplicant} data-testid="button-add-sample-applicant-slate">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add applicant
              </Button>
            </div>
            {sampleApplicants.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="text-sample-applicants-empty-slate">
                No custom applicants — the tool returns the built-in SUNY sample records.
              </p>
            ) : (
              <div className="space-y-3">
                {sampleApplicants.map((row, i) => (
                  <div key={i} className="rounded-md border bg-muted/30 p-3 space-y-2" data-testid={`row-sample-applicant-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Applicant {i + 1}</span>
                      <Button
                        type="button" variant="ghost" size="sm" className="h-7 px-2 text-rose-600"
                        onClick={() => removeApplicant(i)}
                        data-testid={`button-remove-sample-applicant-${i}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs" htmlFor={`input-sample-applicant-name-${i}`}>Name</Label>
                        <Input
                          id={`input-sample-applicant-name-${i}`}
                          value={row.name} placeholder="Maya Thompson"
                          onChange={(e) => updateApplicant(i, "name", e.target.value)}
                          data-testid={`input-sample-applicant-name-${i}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" htmlFor={`input-sample-applicant-email-${i}`}>Email</Label>
                        <Input
                          id={`input-sample-applicant-email-${i}`}
                          value={row.email} placeholder="maya.thompson@example.com"
                          onChange={(e) => updateApplicant(i, "email", e.target.value)}
                          data-testid={`input-sample-applicant-email-${i}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" htmlFor={`input-sample-applicant-campus-${i}`}>Campus</Label>
                        <Input
                          id={`input-sample-applicant-campus-${i}`}
                          value={row.campus} placeholder="University at Buffalo"
                          onChange={(e) => updateApplicant(i, "campus", e.target.value)}
                          data-testid={`input-sample-applicant-campus-${i}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" htmlFor={`input-sample-applicant-program-${i}`}>Program</Label>
                        <Input
                          id={`input-sample-applicant-program-${i}`}
                          value={row.program} placeholder="B.S. Computer Science"
                          onChange={(e) => updateApplicant(i, "program", e.target.value)}
                          data-testid={`input-sample-applicant-program-${i}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" htmlFor={`input-sample-applicant-status-${i}`}>Status</Label>
                        <Input
                          id={`input-sample-applicant-status-${i}`}
                          value={row.status} placeholder="Admitted"
                          onChange={(e) => updateApplicant(i, "status", e.target.value)}
                          data-testid={`input-sample-applicant-status-${i}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" htmlFor={`input-sample-applicant-decision-${i}`}>Decision</Label>
                        <Input
                          id={`input-sample-applicant-decision-${i}`}
                          value={row.decision} placeholder="Admit"
                          onChange={(e) => updateApplicant(i, "decision", e.target.value)}
                          data-testid={`input-sample-applicant-decision-${i}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={saveMut.isPending || !realRequiredOk}
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-slate"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Clever (K-12 rostering / SSO) card — bearer district-data token, no
// instance URL. Mock mode reads the per-org demo rostering tables.
// ──────────────────────────────────────────────────────────────────────

function CleverCard({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [name, setName] = useState(existing?.name ?? "Clever");
  const [accessToken, setAccessToken] = useState("");
  const [mock, setMock] = useState(true);

  useEffect(() => {
    if (!existing) return;
    setName((prev) => (prev && prev !== "Clever" ? prev : existing.name || "Clever"));
    if (typeof existing.mock === "boolean") setMock(existing.mock);
  }, [existing?.id]);

  const saveMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "clever",
      name,
      config: { mock, accessToken: accessToken.trim() },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Clever");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setAccessToken("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Clever");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "Clever responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Integration removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setName("Clever"); setAccessToken(""); setMock(true);
    },
  });

  const realRequiredOk = mock || existing?.configured || accessToken.trim().length > 0;

  return (
    <Card data-testid="card-integration-clever">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">Clever
              {existing && <StatusPill status={existing.status} />}
              {mock && <Badge variant="secondary" data-testid="badge-mock-clever">Mock</Badge>}
            </CardTitle>
            <CardDescription>
              K-12 rostering &amp; SSO. Look up students, list schools and sections, and pull section rosters from chat.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-clever">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-clever">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-clever">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-clever">{existing.lastError}</span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="cl-name">Display name</Label>
            <Input id="cl-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-clever" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cl-token">District data token</Label>
            <Input id="cl-token" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={accessToken} onChange={(e) => setAccessToken(e.target.value)} data-testid="input-access-token-clever" />
          </div>
        </div>
        <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="text-clever-help">
          <strong>Set-up:</strong> in the Clever Dashboard → Data Browser, generate a district data access token
          (bearer) with read access to schools, sections and students, then paste it above.
        </div>
        <div className="flex items-center gap-3">
          <Switch id="cl-mock" checked={mock} onCheckedChange={setMock} data-testid="switch-mock-clever" />
          <Label htmlFor="cl-mock" className="cursor-pointer">
            Use mock mode (no outbound API calls — returns sample schools / students, perfect for demo / setup)
          </Label>
        </div>
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={saveMut.isPending || !realRequiredOk}
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-clever"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Canvas LMS (Instructure) card — instance URL + access token. Mock mode
// reads/writes the per-org demo LMS tables (courses, assignments, grades,
// enrollments).
// ──────────────────────────────────────────────────────────────────────

function CanvasCard({ existing }: { existing?: IntegrationDTO }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [name, setName] = useState(existing?.name ?? "Canvas");
  const [instanceUrl, setInstanceUrl] = useState(existing?.instanceUrl ?? "");
  const [accessToken, setAccessToken] = useState("");
  const [mock, setMock] = useState(true);

  useEffect(() => {
    if (!existing) return;
    setName((prev) => (prev && prev !== "Canvas" ? prev : existing.name || "Canvas"));
    setInstanceUrl((prev) => prev || existing.instanceUrl || "");
    if (typeof existing.mock === "boolean") setMock(existing.mock);
  }, [existing?.id]);

  const saveMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "canvas",
      name,
      instanceUrl: instanceUrl.trim(),
      config: { mock, baseUrl: instanceUrl.trim(), accessToken: accessToken.trim() },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Canvas");
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      setAccessToken("");
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/integrations/${id}/test`),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, "Canvas");
      toast({ title: verdict.ok ? "Connection OK" : verdict.title, description: verdict.ok ? "Canvas responded successfully." : verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/integrations/${id}`),
    onSuccess: () => {
      toast({ title: "Integration removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      setName("Canvas"); setInstanceUrl(""); setAccessToken(""); setMock(true);
    },
  });

  const realRequiredOk = mock || (
    instanceUrl.trim().length > 0 && (existing?.configured || accessToken.trim().length > 0)
  );

  return (
    <Card data-testid="card-integration-canvas">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">Canvas
              {existing && <StatusPill status={existing.status} />}
              {mock && <Badge variant="secondary" data-testid="badge-mock-canvas">Mock</Badge>}
            </CardTitle>
            <CardDescription>
              Canvas LMS (Instructure). List courses &amp; assignments, look up student grades, and enroll students from chat.
            </CardDescription>
          </div>
          {existing && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={testMut.isPending} onClick={() => testMut.mutate(existing.id)} data-testid="button-test-connection-canvas">
                <RefreshCcw className="w-4 h-4 mr-1" /> Test
              </Button>
              <Button variant="outline" size="sm" className="text-rose-600" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(existing.id)} data-testid="button-delete-integration-canvas">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {existing && (
          <div className="text-xs text-muted-foreground" data-testid="text-last-checked-canvas">
            Last checked: {existing.lastCheckedAt ? new Date(existing.lastCheckedAt).toLocaleString() : "never"}
            {existing.lastError ? (
              <span className="block text-rose-600 dark:text-rose-400 mt-1" data-testid="text-last-error-canvas">{existing.lastError}</span>
            ) : null}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="cv-name">Display name</Label>
            <Input id="cv-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-name-canvas" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cv-url">Canvas URL</Label>
            <Input id="cv-url" placeholder="https://school.instructure.com" value={instanceUrl} onChange={(e) => setInstanceUrl(e.target.value)} data-testid="input-instance-url-canvas" />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="cv-token">Access token</Label>
            <Input id="cv-token" type="password" placeholder={existing?.configured ? "•••••• (saved)" : ""} value={accessToken} onChange={(e) => setAccessToken(e.target.value)} data-testid="input-access-token-canvas" />
          </div>
        </div>
        <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="text-canvas-help">
          <strong>Set-up:</strong> in Canvas → Account → Settings → Approved Integrations, create a new access token,
          then paste your Canvas URL and the token above.
        </div>
        <div className="flex items-center gap-3">
          <Switch id="cv-mock" checked={mock} onCheckedChange={setMock} data-testid="switch-mock-canvas" />
          <Label htmlFor="cv-mock" className="cursor-pointer">
            Use mock mode (no outbound API calls — returns sample courses / grades, perfect for demo / setup)
          </Label>
        </div>
        <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
        <div className="flex justify-end">
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={saveMut.isPending || !realRequiredOk}
            onClick={() => saveMut.mutate()}
            data-testid="button-save-integration-canvas"
          >
            {saveMut.isPending ? "Saving…" : existing ? "Update connection" : "Save & test"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Add Integration dialog
// ──────────────────────────────────────────────────────────────────────

function AddIntegrationDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [authType, setAuthType] = useState<AuthType>("none");
  const [headerName, setHeaderName] = useState("");
  const [headerValue, setHeaderValue] = useState("");
  const [bearerToken, setBearerToken] = useState("");
  const [basicUsername, setBasicUsername] = useState("");
  const [basicPassword, setBasicPassword] = useState("");
  const [testPath, setTestPath] = useState("/");
  const [successStatuses, setSuccessStatuses] = useState("");
  const [headers, setHeaders] = useState<HeaderRow[]>([]);

  function reset() {
    setName(""); setBaseUrl(""); setAuthType("none");
    setHeaderName(""); setHeaderValue(""); setBearerToken("");
    setBasicUsername(""); setBasicPassword("");
    setTestPath("/"); setSuccessStatuses(""); setHeaders([]);
  }

  const createMut = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/chatbots/${chatbotId}/integrations`, {
      type: "custom",
      name,
      instanceUrl: baseUrl,
      config: {
        authType,
        authConfig:
          authType === "api_key" ? { headerName, headerValue } :
          authType === "bearer" ? { token: bearerToken } :
          authType === "basic" ? { username: basicUsername, password: basicPassword } :
          {},
        headers: headers.filter((h) => h.name.trim().length > 0).map((h) => ({ name: h.name, value: h.value, secret: h.secret })),
        testPath,
        successStatuses: successStatuses
          .split(/[,\s]+/)
          .map((s) => Number(s))
          .filter((n) => Number.isInteger(n) && n >= 100 && n < 600),
      },
    }),
    onSuccess: async (res) => {
      const json = await readSaveResponse(res);
      const verdict = describeTestResult(json?.test, name);
      toast({ title: verdict.title, description: verdict.description, variant: verdict.ok ? "default" : "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "integrations"] });
      reset();
      onOpenChange(false);
    },
    onError: (err: Error) => toast({ title: "Add failed", description: err.message, variant: "destructive" }),
  });

  const requiredOk =
    name.trim().length > 0 && baseUrl.trim().length > 0 &&
    (authType !== "api_key" || (headerName.trim() && headerValue)) &&
    (authType !== "bearer" || bearerToken) &&
    (authType !== "basic" || (basicUsername.trim() && basicPassword));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="heading-add-integration">Add integration</DialogTitle>
          <DialogDescription>
            Connect any REST API. We test reachability with a GET probe and store credentials encrypted at rest.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="add-name">Display name <span className="text-rose-500">*</span></Label>
              <Input id="add-name" placeholder="Salesforce Prod" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-add-name" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="add-url">Base URL <span className="text-rose-500">*</span></Label>
              <Input id="add-url" placeholder="https://api.salesforce.com" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} data-testid="input-add-base-url" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="add-auth">Authentication</Label>
              <Select value={authType} onValueChange={(v) => setAuthType(v as AuthType)}>
                <SelectTrigger id="add-auth" data-testid="select-add-auth-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="api_key">API key (header)</SelectItem>
                  <SelectItem value="bearer">Bearer token</SelectItem>
                  <SelectItem value="basic">Basic auth</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="add-tp">Test path</Label>
              <Input id="add-tp" placeholder="/" value={testPath} onChange={(e) => setTestPath(e.target.value)} data-testid="input-add-test-path" />
            </div>
          </div>
          {authType === "api_key" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="input-add-header-name">Header name <span className="text-rose-500">*</span></Label>
                <Input id="input-add-header-name" placeholder="X-API-Key" value={headerName} onChange={(e) => setHeaderName(e.target.value)} data-testid="input-add-header-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="input-add-header-value">Header value <span className="text-rose-500">*</span></Label>
                <Input id="input-add-header-value" type="password" value={headerValue} onChange={(e) => setHeaderValue(e.target.value)} data-testid="input-add-header-value" />
              </div>
            </div>
          )}
          {authType === "bearer" && (
            <div className="space-y-1">
              <Label htmlFor="input-add-bearer-token">Bearer token <span className="text-rose-500">*</span></Label>
              <Input id="input-add-bearer-token" type="password" value={bearerToken} onChange={(e) => setBearerToken(e.target.value)} data-testid="input-add-bearer-token" />
            </div>
          )}
          {authType === "basic" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="input-add-basic-username">Username <span className="text-rose-500">*</span></Label>
                <Input id="input-add-basic-username" value={basicUsername} onChange={(e) => setBasicUsername(e.target.value)} data-testid="input-add-basic-username" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="input-add-basic-password">Password <span className="text-rose-500">*</span></Label>
                <Input id="input-add-basic-password" type="password" value={basicPassword} onChange={(e) => setBasicPassword(e.target.value)} data-testid="input-add-basic-password" />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Custom headers</Label>
              <Button variant="ghost" size="sm" onClick={() => setHeaders([...headers, { name: "", value: "", secret: false }])} data-testid="button-add-add-header">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add header
              </Button>
            </div>
            {headers.map((h, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
                <Input placeholder="Header name" value={h.name} onChange={(e) => { const n = [...headers]; n[idx] = { ...n[idx], name: e.target.value }; setHeaders(n); }} data-testid={`input-add-custom-header-name-${idx}`} />
                <Input type={h.secret ? "password" : "text"} placeholder="Header value" value={h.value} onChange={(e) => { const n = [...headers]; n[idx] = { ...n[idx], value: e.target.value }; setHeaders(n); }} data-testid={`input-add-custom-header-value-${idx}`} />
                <label htmlFor={`switch-add-custom-header-secret-${idx}`} className="flex items-center gap-1 text-xs">
                  <Switch id={`switch-add-custom-header-secret-${idx}`} checked={h.secret} onCheckedChange={(s) => { const n = [...headers]; n[idx] = { ...n[idx], secret: s }; setHeaders(n); }} data-testid={`switch-add-custom-header-secret-${idx}`} />
                  Secret
                </label>
                <IconButton variant="ghost" label={h.name ? `Remove header ${h.name}` : `Remove header #${idx + 1}`} onClick={() => setHeaders(headers.filter((_, i) => i !== idx))} data-testid={`button-add-remove-header-${idx}`}>
                  <X className="w-4 h-4" aria-hidden="true" />
                </IconButton>
              </div>
            ))}
          </div>
          <div className="space-y-1">
            <Label htmlFor="add-ss">Success status codes</Label>
            <Input id="add-ss" placeholder="200, 204" value={successStatuses} onChange={(e) => setSuccessStatuses(e.target.value)} data-testid="input-add-success-statuses" />
            <p className="text-xs text-muted-foreground">Optional. Comma-separated; defaults to any 2xx.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-add-integration">Cancel</Button>
          <Button
            className="bg-[#1FA8E0] hover:bg-[#1A91C2]"
            disabled={createMut.isPending || !requiredOk}
            onClick={() => createMut.mutate()}
            data-testid="button-save-add-integration"
          >
            {createMut.isPending ? "Saving…" : "Save & test"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────

interface ChatbotMini { id: string; name: string }

// ──────────────────────────────────────────────────────────────────────
// AI Provider Keys (per-chatbot)
// ──────────────────────────────────────────────────────────────────────
// Each chatbot owns its own provider keys (OpenAI, Anthropic, etc.).
// Keys are write-only at rest — the list endpoint returns a masked
// preview; revealing requires GET /revealed/{id}. Saving a key triggers
// a server-side model probe whose result powers the chatbot Configure
// tab's model dropdown via /api/chatbots/{id}/all-models.

interface AiProviderInfo {
  id: string;
  name: string;
  keyPlaceholder: string;
  keyHelpUrl: string;
  fallbackModels: string[];
}

interface AiKeyEntry {
  id: string;
  provider: string;
  apiKey: string;        // masked preview from server
  detectedModels: string | null;
  isActive: string | null;
  createdAt: string | null;
}

function AiProviderKeysCard() {
  const chatbotId = useChatbotId();
  const { toast } = useToast();
  const { data: providers } = useQuery<AiProviderInfo[]>({ queryKey: ["/api/ai-providers"] });
  const { data: keys } = useQuery<AiKeyEntry[]>({
    queryKey: ["/api/chatbots", chatbotId, "ai-keys"],
    enabled: !!chatbotId,
  });

  const [addingProvider, setAddingProvider] = useState("");
  const [newKey, setNewKey] = useState("");
  const [revealedId, setRevealedId] = useState<string | null>(null);
  const [revealedValue, setRevealedValue] = useState<string | null>(null);

  const configured = new Set((keys ?? []).map(k => k.provider));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "ai-keys"] });
    queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "all-models"] });
    // Also refresh the Approvals queue so the new pending request appears immediately.
    queryClient.invalidateQueries({ queryKey: ["/api/approvals"] });
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/chatbots/${chatbotId}/ai-keys`, {
        provider: addingProvider,
        apiKey: newKey,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? body?.message ?? `Server error ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      const name = providers?.find(p => p.id === data.provider)?.name ?? data.provider;
      const isPending = data.isActive === "pending_approval";
      toast({
        title: isPending ? `${name} key submitted for approval` : `${name} key saved`,
        description: isPending
          ? "An OrgAdmin must approve this key in the Approvals menu before it becomes active."
          : `Detected ${Array.isArray(data.detectedModels) ? data.detectedModels.length : 0} models`,
      });
      setNewKey(""); setAddingProvider("");
      invalidate();
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const refreshMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/chatbots/${chatbotId}/ai-keys/${id}/refresh`, {});
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: "Models refreshed", description: `Found ${data.detectedModels?.length ?? 0} models` });
      invalidate();
    },
    onError: (err: Error) => toast({ title: "Refresh failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/chatbots/${chatbotId}/ai-keys/${id}/test`);
      return res.json();
    },
    onSuccess: (data: any) => {
      const name = providers?.find(p => p.id === data.provider)?.name ?? data.provider;
      if (data.verifiable === false) {
        toast({ title: `${name} can't be verified`, description: "This provider has no endpoint to check the key against; it will be used as saved." });
      } else if (data.ok) {
        toast({ title: `${name} connection OK`, description: "The provider accepted this API key." });
      } else {
        toast({ title: `${name} connection failed`, description: data.error ?? "Unknown error", variant: "destructive" });
      }
    },
    onError: (err: Error) => toast({ title: "Test failed", description: err.message, variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/chatbots/${chatbotId}/ai-keys/${id}`),
    onSuccess: () => { toast({ title: "Key removed" }); invalidate(); },
  });

  return (
    <Card data-testid="card-ai-provider-keys">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">AI Model Providers</CardTitle>
            <CardDescription>
              API keys for any LLM provider. Each chatbot has its own keys; saving a key probes the provider for available models, which then appear in this bot's model dropdown on the Configure tab.
            </CardDescription>
          </div>
          <Badge variant="outline" data-testid="badge-bot-provider-count">
            {keys?.length ?? 0} configured
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {keys?.map((key) => {
          const info = providers?.find(p => p.id === key.provider);
          let models: string[] = [];
          try { models = key.detectedModels ? JSON.parse(key.detectedModels) : []; } catch { /* ignore */ }
          const isRevealed = revealedId === key.id;
          return (
            <div key={key.id} className="p-3 rounded-lg border bg-muted/30" data-testid={`bot-provider-card-${key.provider}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{info?.name ?? key.provider}</span>
                  {key.isActive === "true" ? (
                    <Badge variant="default" className="text-[10px]">
                      <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Active
                    </Badge>
                  ) : key.isActive === "pending_approval" ? (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                      <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Pending Approval
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px]">Inactive</Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline" size="sm" className="h-7"
                    disabled={testMut.isPending}
                    onClick={() => testMut.mutate(key.id)}
                    data-testid={`button-test-connection-${key.provider}`}
                  >
                    <Plug className="w-3.5 h-3.5 mr-1" /> Test
                  </Button>
                  <IconButton
                    variant="ghost" className="h-7 w-7"
                    label={isRevealed ? `Hide ${info?.name ?? key.provider} API key` : `Reveal ${info?.name ?? key.provider} API key`}
                    data-testid={`button-reveal-${key.provider}`}
                    onClick={async () => {
                      if (isRevealed) { setRevealedId(null); setRevealedValue(null); return; }
                      try {
                        const res = await apiRequest("GET", `/api/chatbots/${chatbotId}/ai-keys/${key.id}/revealed`);
                        const data = await res.json();
                        setRevealedId(key.id);
                        setRevealedValue(data.apiKey);
                      } catch {
                        toast({ title: "Could not reveal key", variant: "destructive" });
                      }
                    }}
                  >
                    {isRevealed ? <X className="w-3.5 h-3.5" aria-hidden="true" /> : <RefreshCcw className="w-3.5 h-3.5" aria-hidden="true" />}
                  </IconButton>
                  <IconButton
                    variant="ghost" className="h-7 w-7"
                    label={`Refresh ${info?.name ?? key.provider} models`}
                    disabled={refreshMut.isPending}
                    onClick={() => refreshMut.mutate(key.id)}
                    data-testid={`button-refresh-${key.provider}`}
                  >
                    <RefreshCcw className={`w-3.5 h-3.5 ${refreshMut.isPending ? "animate-spin" : ""}`} aria-hidden="true" />
                  </IconButton>
                  <IconButton
                    variant="ghost" className="h-7 w-7 text-rose-600"
                    label={`Remove ${info?.name ?? key.provider} API key`}
                    disabled={deleteMut.isPending}
                    onClick={() => { if (confirm(`Remove ${info?.name ?? key.provider} API key?`)) deleteMut.mutate(key.id); }}
                    data-testid={`button-delete-${key.provider}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                  </IconButton>
                </div>
              </div>
              <p className="text-xs font-mono mb-2" data-testid={`text-key-${key.provider}`}>
                {isRevealed && revealedValue ? revealedValue : key.apiKey}
              </p>
              {models.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {models.slice(0, 8).map((m) => (
                    <Badge key={m} variant="secondary" className="text-[10px]">{m}</Badge>
                  ))}
                  {models.length > 8 && (
                    <Badge variant="outline" className="text-[10px]">+{models.length - 8} more</Badge>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {(!keys || keys.length === 0) && (
          <div className="text-center py-6 text-muted-foreground" data-testid="empty-bot-providers">
            <p className="text-sm">No AI providers configured for this chatbot yet.</p>
            <p className="text-xs">Add a provider key below to enable live chat.</p>
          </div>
        )}

        <div className="border-t pt-4 space-y-3">
          <Label className="text-sm font-semibold">Add AI provider</Label>
          <div className="flex items-start gap-3">
            <div className="w-48">
              <Select value={addingProvider} onValueChange={(v) => { setAddingProvider(v); setNewKey(""); }}>
                <SelectTrigger data-testid="select-bot-provider">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers?.map((p) => (
                    <SelectItem key={p.id} value={p.id} disabled={configured.has(p.id)}>
                      {p.name} {configured.has(p.id) ? "✓" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {addingProvider && (
              <div className="flex-1 space-y-2">
                <Input
                  type="password"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder={providers?.find(p => p.id === addingProvider)?.keyPlaceholder ?? "Enter API key"}
                  data-testid="input-bot-provider-key"
                />
                <InlineFormError error={saveMut.isError ? saveMut.error : null} className="justify-end text-right" />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { setAddingProvider(""); setNewKey(""); }}
                    data-testid="button-cancel-bot-provider"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={!newKey || saveMut.isPending}
                    onClick={() => saveMut.mutate()}
                    data-testid="button-save-bot-provider-key"
                  >
                    {saveMut.isPending ? "Validating…" : "Save key"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Groups related connectors under a labelled heading so the page reads as
// "all the IT help desk tools here, all the HR tools there", etc. New
// connectors slot into an existing category by dropping the card inside the
// matching <IntegrationCategory> below.
function IntegrationCategory({
  icon: Icon,
  label,
  description,
  children,
}: {
  icon: typeof Plug;
  label: string;
  description: string;
  children: ReactNode;
}) {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <section className="space-y-4" data-testid={`category-${slug}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg brand-pill shrink-0">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight text-foreground" data-testid={`category-title-${slug}`}>{label}</h2>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export default function ChatbotIntegrationsPage() {
  const params = useParams<{ id: string }>();
  const chatbotId = params.id ?? "";
  const brandShort = useBranding().shortName;
  const { data: bot } = useQuery<ChatbotMini>({ queryKey: ["/api/chatbots", chatbotId], enabled: !!chatbotId });
  const { data: integrations = [], isLoading } = useQuery<IntegrationDTO[]>({
    queryKey: ["/api/chatbots", chatbotId, "integrations"],
    enabled: !!chatbotId,
  });
  const [addOpen, setAddOpen] = useState(false);

  const servicenow = integrations.find((i) => i.type === "servicenow");
  const jira = integrations.find((i) => i.type === "jira");
  const teamdynamix = integrations.find((i) => i.type === "teamdynamix");
  const workday = integrations.find((i) => i.type === "workday");
  const zendesk = integrations.find((i) => i.type === "zendesk");
  const twilio = integrations.find((i) => i.type === "twilio");
  const dynamics365 = integrations.find((i) => i.type === "dynamics365");
  const salesforce = integrations.find((i) => i.type === "salesforce");
  const m365 = integrations.find((i) => i.type === "m365");
  const slate = integrations.find((i) => i.type === "slate");
  const clever = integrations.find((i) => i.type === "clever");
  const canvas = integrations.find((i) => i.type === "canvas");
  const customs = integrations.filter((i) => i.type.startsWith("custom:"));
  const { toast } = useToast();

  // Surface the result of the Zendesk OAuth round-trip. The callback
  // route redirects back here with ?zendesk=connected|error|denied (and
  // an optional ?zendesk_code= enum), so we pop a toast and scrub the
  // params from the URL on first render. The detailed upstream error
  // (if any) lives on the integration row's `lastError` instead of the
  // URL, so admins still see it without it ending up in browser history.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const status = params.get("zendesk");
    if (!status) return;
    const code = params.get("zendesk_code") ?? undefined;
    const friendly: Record<string, string> = {
      missing: "Zendesk redirected back without an authorization code.",
      csrf: "Security check failed (CSRF state mismatch). Please retry the connection.",
      expired: "The authorization session expired. Please retry within 15 minutes.",
      exchange_failed: "Could not exchange the authorization code. Check the card for the upstream error.",
      secret_mismatch: "Zendesk accepted the Client ID but rejected the Client Secret. Copy the Secret for this OAuth client from Zendesk Admin Center (regenerate it if it's no longer shown), paste it on the card, save, then reconnect.",
      integration_missing: "The Zendesk card hasn't been saved for this chatbot yet. Save the subdomain and OAuth credentials first.",
      oauth_client_missing: "The saved Zendesk card is missing the OAuth Client ID or Secret. Add them, save, then reconnect.",
      no_refresh_token: "Connected, but Zendesk didn't issue a refresh token, so the connection can't auto-renew and will drop when the token expires. Enable token expiration (rotating refresh tokens) on this OAuth client in Zendesk Admin Center, then click Re-authorise Zendesk. Your existing Client ID and Secret stay the same.",
      denied: "You cancelled the Zendesk authorization screen.",
      unknown: "Zendesk reported an unknown error.",
    };
    if (status === "connected" && code === "no_refresh_token") {
      toast({ title: "Zendesk connected — action needed", description: friendly.no_refresh_token, variant: "destructive" });
    } else if (status === "connected") {
      toast({ title: "Zendesk connected", description: "OAuth authorisation completed successfully." });
    } else if (status === "denied") {
      toast({ title: "Zendesk authorisation cancelled", description: friendly.denied, variant: "destructive" });
    } else {
      toast({ title: "Zendesk connection failed", description: code ? friendly[code] ?? friendly.unknown : friendly.unknown, variant: "destructive" });
    }
    params.delete("zendesk");
    params.delete("zendesk_code");
    const next = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${next ? `?${next}` : ""}`);
  }, [toast]);

  const connectedCount = integrations.filter((i) => i.status === "connected").length;
  const errorCount = integrations.filter((i) => i.status === "error" || i.status === "token_expired").length;

  return (
    <ChatbotIdContext.Provider value={chatbotId}>
    <PageShell
      className="max-w-5xl"
      eyebrow={<Link href={`/chatbots/${chatbotId}`} className="hover:underline" data-testid="link-back-to-chatbot">← {bot?.name ?? "Chatbot"}</Link>}
      title={<span className="flex items-center gap-2" data-testid="heading-integrations"><Plug className="w-5 h-5 text-[#1FA8E0]" /> Integrations</span>}
      subtitle={`Connect this chatbot to ${brandShort}'s back-office systems so it can take real action on behalf of employees. Each chatbot has its own credentials.`}
      actions={
        <>
          <Button
            className="bg-[#0C3547] hover:bg-[#0A2D3A] text-white"
            onClick={() => setAddOpen(true)}
            data-testid="button-add-integration"
          >
            <Plus className="w-4 h-4 mr-1" /> Add integration
          </Button>
        </>
      }
      stats={
        <>
          <PageStat
            label="Total"
            value={integrations.length}
            hint="All connectors"
            icon={<Plug className="w-4 h-4" />}
          />
          <PageStat
            label="Connected"
            value={connectedCount}
            hint={connectedCount === integrations.length && integrations.length > 0 ? "All healthy" : "Live & working"}
            icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />}
          />
          <PageStat
            label="Needs attention"
            value={errorCount}
            hint={errorCount === 0 ? "No issues" : "Re-auth or fix required"}
            icon={<AlertTriangle className={`w-4 h-4 ${errorCount ? "text-amber-600" : ""}`} />}
          />
          <PageStat
            label="Custom"
            value={customs.length}
            hint="REST connectors"
            icon={<Code2 className="w-4 h-4" />}
          />
        </>
      }
    >
      <AiProviderKeysCard />

      <IntegrationCategory
        icon={LifeBuoy}
        label="IT Help Desk"
        description="ITSM platforms for incidents, service requests, and the IT knowledge base."
      >
        <ServiceNowCard existing={servicenow} />
        <JiraCard existing={jira} />
        <TeamDynamixCard existing={teamdynamix} />
      </IntegrationCategory>

      <IntegrationCategory
        icon={Headphones}
        label="Customer Support & CRM"
        description="Customer service desks and CRM systems for tickets and customer records."
      >
        <ZendeskCard existing={zendesk} />
        <Dynamics365Card existing={dynamics365} />
        <SalesforceCard existing={salesforce} />
      </IntegrationCategory>

      <IntegrationCategory
        icon={CalendarClock}
        label="Productivity & Calendar"
        description="Microsoft 365 directory lookups and Outlook calendar / availability."
      >
        <Microsoft365Card existing={m365} />
      </IntegrationCategory>

      <IntegrationCategory
        icon={GraduationCap}
        label="Admissions & Enrollment"
        description="Student information and admissions CRM for application status and inquiries."
      >
        <SlateCard existing={slate} />
      </IntegrationCategory>

      <IntegrationCategory
        icon={School}
        label="Rostering & LMS"
        description="K-12 rostering / SSO and the learning management system for courses, grades and enrollment."
      >
        <CleverCard existing={clever} />
        <CanvasCard existing={canvas} />
      </IntegrationCategory>

      <IntegrationCategory
        icon={Users}
        label="HR & People"
        description="HR systems for time off, payroll, and the employee directory."
      >
        <WorkdayCard existing={workday} />
      </IntegrationCategory>

      <IntegrationCategory
        icon={Phone}
        label="Communications"
        description="Telephony and messaging providers for voice and SMS."
      >
        <TwilioCard existing={twilio} />
      </IntegrationCategory>

      {customs.length > 0 && (
        <IntegrationCategory
          icon={Code2}
          label="Custom Integrations"
          description="Your own REST connectors."
        >
          {customs.map((i) => <CustomCard key={i.id} existing={i} />)}
        </IntegrationCategory>
      )}

      {isLoading && <p className="text-sm text-muted-foreground">Loading integrations…</p>}

      <AddIntegrationDialog open={addOpen} onOpenChange={setAddOpen} />
    </PageShell>
    </ChatbotIdContext.Provider>
  );
}
